import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import UploadDataModal from "../components/UploadDataModal";
import ExcelProcessingTracker from "../components/ExcelProcessingTracker";
import CreateHouseholdsModal from "../components/CreateHouseholdsModal";
import ExportProjectDataModal from "../components/ExportProjectDataModal";
import HouseholdSetupWizard from "../components/HouseholdSetupWizard/HouseholdSetupWizard";
import StateCitySelect from "../components/StateCitySelect";
import axiosInstance from "../api/axiosInstance";
import {
  validateHouseholdData,
  validateAccountData,
} from "../api/updateHouseholdAPI";
import { formatDateForDisplay, formatCompactCurrency } from "../utils/formatters";
import { resolveStateName } from "../utils/stateUtils";
import { validateDob, getTodayYmdLocal } from "../utils/dateFieldConstraints";
import {
  fileExceedsMaxUploadSize,
  maxUploadSizeExceededMessage,
  MAX_UPLOAD_FILE_SIZE_LABEL,
} from "../utils/uploadFileSizeLimit";
import StatusBadge from "../components/StatusBadge";
import SavedFiltersPanel from "../components/SavedFiltersPanel";
import NeedsAttentionView from "../components/NeedsAttention/NeedsAttentionView";
import {
  listSavedFilters,
  buildFiltersFromUiState,
  applyFiltersToUiState,
} from "../api/savedFiltersAPI";
import {
  STATUS_FILTER_OPTIONS,
  labelForStatus,
} from "../constants/householdStatus";
import { TAX_BRACKET_OPTIONS, TAX_BRACKET_MAP } from "../constants/householdConstants";
import { getAccountBanks } from "../utils/bankAccounts";
import BankAccountAccordion from "../components/BankAccountAccordion";
import { useToast } from "../context/ToastContext";
import { useNotifications } from "../context/NotificationContext";
import { useProjectMembers } from "../context/ProjectMembersContext";
import { humanizeError } from "../utils/userFriendlyError";
import {
  readProjectViewState,
  writeProjectViewState,
} from "../utils/projectViewState";
import {
  getBrokerageFlags,
  showAnnualExpenseRange,
  showSpecialExpenseRange,
  showIdInfo,
  showMaritalStatus,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
  showExperienceField,
  showIncomeDetails,
} from "../utils/brokerageFields";
import { COUNTRY_OPTIONS } from "../constants/projectConstants";
import {
  ArrowRight,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  CreditCard,
  FileDown,
  FileText,
  Home,
  Loader2,
  Mail,
  Menu,
  MessageCircle,
  Pencil,
  PieChart,
  Plus,
  RefreshCw,
  Search,
  TriangleAlert,
  Upload,
  UploadCloud,
  User,
  Users,
  X,
} from "lucide-react";

const PROJECT_DETAIL_PRIVILEGED_EMAILS = (
  process.env.REACT_APP_PROJECT_DETAIL_PRIVILEGED_EMAILS || ""
)
  .split(",")
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

function NeedsAttentionMark({ active }) {
  // Always reserve the same width column so names align across rows.
  return (
    <span
      className="inline-flex items-center justify-center w-4 text-red-600 font-extrabold text-lg leading-none flex-shrink-0"
      title={active ? "In Needs Attention" : undefined}
      aria-label={active ? "In Needs Attention" : undefined}
    >
      {active && (
        <span className="motion-safe:animate-[bounce_0.7s_ease-in-out_3]">!</span>
      )}
    </span>
  );
}

function CommentMark({ commentState, unreadCount = 0 }) {
  const hasUnread = unreadCount > 0 || commentState === "has_unread";
  const hasAnyComments = hasUnread || commentState === "all_read";
  if (!hasAnyComments) return null;

  const unreadLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const title = hasUnread
    ? `${unreadCount} unread comment${unreadCount === 1 ? "" : "s"}`
    : "Comments (all read)";

  return (
    <span
      className="relative inline-flex items-center justify-center text-gray-500 flex-shrink-0"
      title={title}
      aria-label={title}
    >
      <MessageCircle className="w-4 h-4" />
      {hasUnread && unreadCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold leading-none ring-2 ring-white">
          {unreadLabel}
        </span>
      )}
      {hasUnread && unreadCount === 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />
      )}
    </span>
  );
}

function ProjectDetail({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const persistedView = readProjectViewState(projectId);
  const { onDocumentProcessed } = useNotifications();
  const { fetchProjectMembers } = useProjectMembers();
  const [project, setProject] = useState(null);
  const brokerageFlags = getBrokerageFlags(project);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [householdsLoading, setHouseholdsLoading] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("household"); // "household" or "accounts"
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [editingHousehold, setEditingHousehold] = useState(false);
  const [householdFormData, setHouseholdFormData] = useState({});
  const [savingHousehold, setSavingHousehold] = useState(false);
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [memberFormData, setMemberFormData] = useState({});
  const [savingMember, setSavingMember] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [accountFormData, setAccountFormData] = useState({});
  const [savingAccount, setSavingAccount] = useState(false);
  const [missingFields, setMissingFields] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [householdsPerPage] = useState(10);
  const [showHouseholdListView, setShowHouseholdListView] = useState(false);
  const [expandedHouseholds, setExpandedHouseholds] = useState(false);
  const [showDetailedView, setShowDetailedView] = useState(true);
  const [excelTriggerToken, setExcelTriggerToken] = useState(0);
  const [detailedViewPage, setDetailedViewPage] = useState(1);
  const [detailedViewPerPage, setDetailedViewPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState(() => persistedView?.sortBy || "updated_at");
  const [sortOrder, setSortOrder] = useState(() => persistedView?.sortOrder || "desc");
  const [selectedStatus, setSelectedStatus] = useState(() => persistedView?.status || "");
  const [selectedAdvisorId, setSelectedAdvisorId] = useState(() => persistedView?.advisorId || "");
  // Snapshot of the filter/sort values that match the current household list response.
  // Used to detect unapplied changes and signal them on the Search button.
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    q: persistedView?.q || "",
    status: persistedView?.status || "",
    advisorId: persistedView?.advisorId || "",
    sortBy: persistedView?.sortBy || "updated_at",
    sortOrder: persistedView?.sortOrder || "desc",
  }));
  // Dashboard tab: "needs_attention" or "all_households". Restored from the
  // per-project session snapshot on return navigation; defaults to
  // needs_attention on first entry so the user lands on actionable work.
  const [dashboardTab, setDashboardTab] = useState(
    () => persistedView?.tab || "needs_attention"
  );
  const [needsAttentionTotal, setNeedsAttentionTotal] = useState(null);
  // Bumped after the HouseholdSetupWizard closes (whether completed or
  // dismissed). NeedsAttentionView re-fetches its payload whenever this
  // changes so cards drop out of the no-activity bucket as soon as the
  // wizard's save bumps their `updated_at`.
  const [needsAttentionRefreshKey, setNeedsAttentionRefreshKey] = useState(0);
  const [savedViews, setSavedViews] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(true);
  const [savedViewsLoaded, setSavedViewsLoaded] = useState(false);
  const [activeViewId, setActiveViewId] = useState(null);
  const initialFetchAppliedRef = useRef(false);
  const [selectedUploadAdvisorId, setSelectedUploadAdvisorId] = useState("");
  const [selectedUploaderId, setSelectedUploaderId] = useState(""); // Keeping this state but will remove from UI as requested
  const [showUploadDataModal, setShowUploadDataModal] = useState(false);
  const [householdSearchTerm, setHouseholdSearchTerm] = useState(() => persistedView?.q || "");
  const [showFormTypeSelectorForAccount, setShowFormTypeSelectorForAccount] =
    useState(null);
  const [selectedFormType, setSelectedFormType] = useState("");
  const [downloadingAccountId, setDownloadingAccountId] = useState(null);
  const [downloadingHouseholdForms, setDownloadingHouseholdForms] =
    useState(false);
  const [accountFormsData, setAccountFormsData] = useState({});
  const [selectedFormsForAccount, setSelectedFormsForAccount] = useState({});
  const [loadingAccountForms, setLoadingAccountForms] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [missingFieldsExpanded, setMissingFieldsExpanded] = useState(false);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [showSendToClientSuccess, setShowSendToClientSuccess] = useState(false);
  const [sendToClientEmail, setSendToClientEmail] = useState("");
  const [showSendToClientError, setShowSendToClientError] = useState(false);
  const [sendToClientErrorMessage, setSendToClientErrorMessage] = useState("");
  const [showCreateHouseholdModal, setShowCreateHouseholdModal] =
    useState(false);
  const [showEnrichHouseholdModal, setShowEnrichHouseholdModal] =
    useState(false);
  const [enrichHouseholdAdvisorId, setEnrichHouseholdAdvisorId] = useState("");
  const [enrichHouseholdUploading, setEnrichHouseholdUploading] =
    useState(false);
  const [enrichHouseholdDragging, setEnrichHouseholdDragging] = useState(false);
  const [showExportProjectDataModal, setShowExportProjectDataModal] =
    useState(false);

  // Wizard State
  const [showWizard, setShowWizard] = useState(false);
  const [wizardHouseholdId, setWizardHouseholdId] = useState(null);
  const [wizardInitialStatus, setWizardInitialStatus] = useState(null);

  const MAX_BULK_UPLOAD_FILES = parseInt(
    process.env.REACT_APP_MAX_BULK_UPLOAD_FILES || "10",
    10,
  );

  const persistViewSnapshot = (tab, filters) => {
    writeProjectViewState(projectId, {
      tab,
      q: filters.q,
      status: filters.status,
      advisorId: filters.advisorId,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
    });
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load project first (faster, shows UI sooner)
        await fetchProject();
        // Households will be fetched by the useEffect dependent on 'project'
      } catch (error) {
        console.error("Error loading project data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchProjectMembers(projectId);
    }
  }, [projectId, fetchProjectMembers]);

  // Removed auto-fetch on advisor/uploader change as per manual trigger requirement

  const fetchProject = async () => {
    try {
      const response = await axiosInstance.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error("Error fetching project:", error);
      throw error;
    }
  };

  /*
   * Fetch Households with optional overrides.
   */
  const fetchHouseholds = async (
    pageOverride = null,
    filtersOverride = null,
  ) => {
    setHouseholdsLoading(true);
    try {
      const pageToUse = pageOverride !== null ? pageOverride : detailedViewPage;

      // Base params from state
      let sort_by = sortBy;
      let sort_order = sortOrder;
      let q = householdSearchTerm;
      let status = selectedStatus;
      let advisorId = selectedAdvisorId || null;

      // Apply overrides if present
      if (filtersOverride) {
        if (filtersOverride.sort_by !== undefined)
          sort_by = filtersOverride.sort_by;
        if (filtersOverride.sort_order !== undefined)
          sort_order = filtersOverride.sort_order;
        if (filtersOverride.q !== undefined) q = filtersOverride.q;
        if (filtersOverride.status !== undefined)
          status = filtersOverride.status;
        if (filtersOverride.advisor_id !== undefined)
          advisorId = filtersOverride.advisor_id;
      }

      const params = {
        page: pageToUse,
        page_size: detailedViewPerPage,
        sort_by,
        sort_order,
      };

      if (q) params.q = q;
      if (status) params.status = status;
      if (advisorId) params.advisor_id = advisorId;

      const response = await axiosInstance.get(
        `/households/project/${projectId}`,
        {
          params,
        },
      );

      // Handle new paginated response
      if (response.data.items) {
        setHouseholds(response.data.items);
        setTotalItems(response.data.total);
        setTotalPages(response.data.pages);
      } else {
        if (Array.isArray(response.data)) {
          setHouseholds(response.data);
          setTotalItems(response.data.length);
          setTotalPages(1);
        }
      }
    } catch (error) {
      console.error("Error fetching households:", error);
    } finally {
      setHouseholdsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const nextFilters = {
      q: householdSearchTerm,
      status: selectedStatus,
      advisorId: selectedAdvisorId,
      sortBy,
      sortOrder,
    };
    setAppliedFilters(nextFilters);
    persistViewSnapshot(dashboardTab, nextFilters);
    setDetailedViewPage(1);
    fetchHouseholds(1);
  };

  const handleClearFilters = () => {
    setSelectedAdvisorId("");
    setSelectedStatus("");
    setHouseholdSearchTerm("");
    setSortBy("updated_at");
    setSortOrder("desc");
    const clearedFilters = {
      q: "",
      status: "",
      advisorId: "",
      sortBy: "updated_at",
      sortOrder: "desc",
    };
    setAppliedFilters(clearedFilters);
    persistViewSnapshot(dashboardTab, clearedFilters);

    setDetailedViewPage(1);
    // Fetch with reset values
    fetchHouseholds(1, {
      q: "",
      status: "",
      advisor_id: null,
      sort_by: "updated_at",
      sort_order: "desc",
    });
  };

  const hasPendingFilterChanges = useMemo(
    () =>
      appliedFilters.q !== householdSearchTerm ||
      appliedFilters.status !== selectedStatus ||
      appliedFilters.advisorId !== selectedAdvisorId ||
      appliedFilters.sortBy !== sortBy ||
      appliedFilters.sortOrder !== sortOrder,
    [
      appliedFilters,
      householdSearchTerm,
      selectedStatus,
      selectedAdvisorId,
      sortBy,
      sortOrder,
    ],
  );

  const handleRefreshHouseholds = () => {
    fetchHouseholds();
  };

  // Ref so the SSE listener stays registered once but always calls fresh closures.
  const refreshHouseholdsRef = useRef(() => {});
  refreshHouseholdsRef.current = () => {
    fetchHouseholds();
    setNeedsAttentionRefreshKey((k) => k + 1);
  };

  // Auto-refresh the households list on HOUSEHOLD_CREATED SSE notifications.
  useEffect(() => {
    const unsubscribe = onDocumentProcessed(
      `project-detail-household-created-${projectId}`,
      (notifs) => {
        const list = Array.isArray(notifs)
          ? notifs
          : notifs != null
            ? [notifs]
            : [];
        const hasHouseholdCreated = list.some(
          (n) => n?.notification_type === "HOUSEHOLD_CREATED",
        );
        if (hasHouseholdCreated) {
          refreshHouseholdsRef.current();
        }
      },
    );
    return unsubscribe;
  }, [projectId, onDocumentProcessed]);

  // Fetch the user's saved views for this project. Runs in parallel with the
  // project fetch — feature is non-critical so failures are silent.
  useEffect(() => {
    let cancelled = false;
    setSavedViewsLoading(true);
    setSavedViewsLoaded(false);
    initialFetchAppliedRef.current = false;
    listSavedFilters(projectId)
      .then((items) => {
        if (!cancelled) setSavedViews(items);
      })
      .catch(() => {
        /* non-critical — leave the list empty */
      })
      .finally(() => {
        if (!cancelled) {
          setSavedViewsLoading(false);
          setSavedViewsLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Refetch when project loads or pagination changes. On the FIRST run after
  // mount, wait for saved views to finish loading and apply the default view
  // (if any) so the first list response already reflects the user's preset.
  useEffect(() => {
    if (!project?.id) return;
    if (!initialFetchAppliedRef.current) {
      if (!savedViewsLoaded) return;
      initialFetchAppliedRef.current = true;
      const persisted = readProjectViewState(projectId);
      if (persisted) {
        fetchHouseholds(1, {
          q: persisted.q || "",
          status: persisted.status || "",
          advisor_id: persisted.advisorId || null,
          sort_by: persisted.sortBy || "updated_at",
          sort_order: persisted.sortOrder || "desc",
        });
        return;
      }
      const def = savedViews.find((v) => v.is_default);
      if (def) {
        const ui = applyFiltersToUiState(def.filters || {}, project);
        setHouseholdSearchTerm(ui.householdSearchTerm);
        setSelectedStatus(ui.selectedStatus);
        setSelectedAdvisorId(ui.selectedAdvisorId);
        setSortBy(ui.sortBy);
        setSortOrder(ui.sortOrder);
        setAppliedFilters({
          q: ui.householdSearchTerm,
          status: ui.selectedStatus,
          advisorId: ui.selectedAdvisorId,
          sortBy: ui.sortBy,
          sortOrder: ui.sortOrder,
        });
        setActiveViewId(def.id);
        fetchHouseholds(1, {
          q: ui.householdSearchTerm,
          status: ui.selectedStatus,
          advisor_id: ui.selectedAdvisorId || null,
          sort_by: ui.sortBy,
          sort_order: ui.sortOrder,
        });
        return;
      }
    }
    fetchHouseholds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, savedViewsLoaded, detailedViewPage, detailedViewPerPage]);

  // Callbacks bridging the page's filter state to the SavedFiltersPanel.
  const getCurrentFilters = () =>
    buildFiltersFromUiState({
      householdSearchTerm,
      selectedStatus,
      selectedAdvisorId,
      sortBy,
      sortOrder,
    });

  const handleApplyView = (view) => {
    const ui = applyFiltersToUiState(view.filters || {}, project);
    setHouseholdSearchTerm(ui.householdSearchTerm);
    setSelectedStatus(ui.selectedStatus);
    setSelectedAdvisorId(ui.selectedAdvisorId);
    setSortBy(ui.sortBy);
    setSortOrder(ui.sortOrder);
    const appliedView = {
      q: ui.householdSearchTerm,
      status: ui.selectedStatus,
      advisorId: ui.selectedAdvisorId,
      sortBy: ui.sortBy,
      sortOrder: ui.sortOrder,
    };
    setAppliedFilters(appliedView);
    persistViewSnapshot(dashboardTab, appliedView);
    setActiveViewId(view.id);
    setDetailedViewPage(1);
    fetchHouseholds(1, {
      q: ui.householdSearchTerm,
      status: ui.selectedStatus,
      advisor_id: ui.selectedAdvisorId || null,
      sort_by: ui.sortBy,
      sort_order: ui.sortOrder,
    });
  };

  const fetchHouseholdDetails = async (householdId) => {
    setHouseholdLoading(true);
    try {
      const response = await axiosInstance.get(`/households/${householdId}`);
      setSelectedHousehold(response.data);

      // Store missing_fields if present
      if (response.data.missing_fields) {
        setMissingFields(response.data.missing_fields);
      } else {
        setMissingFields(null);
      }

      // Initialize form data with current household data
      setHouseholdFormData({
        annual_income: response.data.annual_income || "",
        estimated_total_net_worth:
          response.data.estimated_total_net_worth || "",
        estimated_liquid_net_worth:
          response.data.estimated_liquid_net_worth || "",
        tax_percentage: response.data.tax_percentage || "",
        annual_expense_range: response.data.annual_expense_range || {
          start: "",
          end: "",
        },
        special_expense_range: response.data.special_expense_range || {
          start: "",
          end: "",
        },
        tax_info: response.data.tax_info || {
          tax_bracket: { start: "", end: "" },
        },
        investor_associations: response.data.investor_associations || {},
        investor_experience: response.data.investor_experience || {},
        status: response.data.status || "active",
      });

      // Fetch forms for all accounts in the household
      if (response.data.accounts && response.data.accounts.length > 0) {
        await fetchAllAccountForms(householdId, response.data.accounts);
      }
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't load household details."));
    } finally {
      setHouseholdLoading(false);
    }
  };

  // Navigation function for missing fields
  const navigateToField = (
    fieldType,
    fieldName,
    memberId = null,
    accountId = null,
  ) => {
    if (!selectedHousehold) return;

    // Small delay to ensure modal is open and rendered
    setTimeout(() => {
      if (fieldType === "household") {
        // Navigate to household section (view mode only)
        const householdFields = [
          "tax_info",
          "estimated_liquid_net_worth",
          "estimated_total_net_worth",
          "annual_income",
          "annual_expense_range",
          "special_expense_range",
        ];
        const investorFields = ["investor_associations", "investor_experience"];

        if (householdFields.includes(fieldName)) {
          // Scroll to Household Information section
          const element = document.querySelector(
            '[data-section="household-info"]',
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        } else if (investorFields.includes(fieldName)) {
          // Scroll to Investor section
          const element = document.querySelector(
            `[data-section="${fieldName}"]`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      } else if (fieldType === "household_members" && memberId) {
        // Navigate to member section (view mode only)
        // Find the member in selectedHousehold
        const member = selectedHousehold.members?.find(
          (m) => m.id === memberId,
        );
        if (member) {
          // Switch to members tab if not already
          setActiveTab("household");
          // Ensure member is not in edit mode
          if (editingMemberId === memberId) {
            setEditingMemberId(null);
          }
          // Scroll to member section
          setTimeout(() => {
            const element = document.querySelector(
              `[data-member-id="${memberId}"]`,
            );
            if (element) {
              element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }, 200);
        }
      } else if (fieldType === "accounts" && accountId) {
        // Navigate to account section (view mode only)
        // Find the account in selectedHousehold
        const account = selectedHousehold.accounts?.find(
          (a) => a.id === accountId,
        );
        if (account) {
          // Switch to accounts tab
          setActiveTab("accounts");
          // Select the account (this opens the detail view)
          setSelectedAccount(account);
          // Ensure account is not in edit mode
          if (editingAccountId === accountId) {
            setEditingAccountId(null);
          }

          // Scroll to account section and specific field
          setTimeout(() => {
            // First scroll to account in list
            const accountElement = document.querySelector(
              `[data-account-id="${accountId}"]`,
            );
            if (accountElement) {
              accountElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }

            // Then scroll to specific field section in account detail view
            setTimeout(() => {
              const accountFieldAliases = {
                beneficiaries_primary_allocation: "beneficiaries",
                beneficiaries_contingent_allocation: "beneficiaries",
                beneficiaries_primary_missing: "beneficiaries",
              };
              const targetField = accountFieldAliases[fieldName] || fieldName;
              const sectionElement = document.querySelector(
                `[data-account-field="${targetField}"]`,
              );
              if (sectionElement) {
                sectionElement.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }, 300);
          }, 200);
        }
      }
    }, 100);
  };

  const fetchAllAccountForms = async (householdId, accounts) => {
    if (!householdId || !accounts || accounts.length === 0) {
      return;
    }

    setLoadingAccountForms(true);
    try {
      const response = await axiosInstance.get(
        `/forms/project/${projectId}/households/${householdId}/forms`,
      );

      const formsData = response.data;

      // Process forms for each account
      accounts.forEach((account) => {
        const accountId = account.id;
        const accountForms = formsData[accountId];

        if (!accountForms) {
          return; // Skip if no forms data for this account
        }

        // Determine if account is IWS or NFS
        const isIWS =
          accountForms.iws !== null && accountForms.iws !== undefined;
        const isNFS =
          accountForms.nfs !== null && accountForms.nfs !== undefined;

        if (!isIWS && !isNFS) {
          return; // Skip if no forms available
        }

        const formsForAccount = isIWS ? accountForms.iws : accountForms.nfs;

        // Store forms data
        setAccountFormsData((prev) => ({
          ...prev,
          [accountId]: {
            type: isIWS ? "iws" : "nfs",
            forms: formsForAccount,
          },
        }));

        // Pre-select forms that are true
        const preSelected = {};
        Object.keys(formsForAccount).forEach((formType) => {
          if (formsForAccount[formType] === true) {
            preSelected[formType] = true;
          }
        });

        setSelectedFormsForAccount((prev) => ({
          ...prev,
          [accountId]: preSelected,
        }));
      });
    } catch (error) {
      console.error("Error fetching account forms:", error);
      // Don't show alert here as it's called automatically
    } finally {
      setLoadingAccountForms(false);
    }
  };

  const handleHouseholdFieldChange = (field, value) => {
    setHouseholdFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAnnualIncomeChange = (value) => {
    let sanitized = value === "" ? "" : parseFloat(value);
    if (sanitized !== "" && (isNaN(sanitized) || sanitized < 0)) sanitized = 0;
    handleHouseholdFieldChange("annual_income", sanitized);
  };

  const handleTotalNetWorthChange = (value) => {
    let sanitized = value === "" ? "" : parseFloat(value);
    if (sanitized !== "" && (isNaN(sanitized) || sanitized < 0)) sanitized = 0;
    handleHouseholdFieldChange("estimated_total_net_worth", sanitized);
  };

  const handleLiquidNetWorthChange = (value) => {
    let sanitized = value === "" ? "" : parseFloat(value);
    if (sanitized !== "" && (isNaN(sanitized) || sanitized < 0)) sanitized = 0;
    handleHouseholdFieldChange("estimated_liquid_net_worth", sanitized);
  };

  const handleTaxPercentageChange = (value) => {
    let sanitized = value === "" ? "" : parseFloat(value);
    if (sanitized !== "" && !isNaN(sanitized)) {
      if (sanitized < 0) sanitized = 0;
      if (sanitized > 100) sanitized = 100;
    }
    handleHouseholdFieldChange("tax_percentage", sanitized);
  };

  const handleExperienceChange = (key, value) => {
    let sanitized = value === "" ? "" : parseFloat(value);
    if (sanitized !== "" && !isNaN(sanitized)) {
      if (sanitized < 0) sanitized = 0;
      if (sanitized > 100) sanitized = 100;
    }
    setHouseholdFormData((prev) => ({
      ...prev,
      investor_experience: {
        ...(prev.investor_experience || {}),
        [key]: sanitized === "" ? null : sanitized,
      },
    }));
  };

  const handleNestedFieldChange = (parentField, nestedField, value) => {
    setHouseholdFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField] || {}),
        [nestedField]: value,
      },
    }));
  };

  const handleDeepNestedFieldChange = (
    parentField,
    nestedField,
    deepField,
    value,
  ) => {
    setHouseholdFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...(prev[parentField] || {}),
        [nestedField]: {
          ...(prev[parentField]?.[nestedField] || {}),
          [deepField]: value,
        },
      },
    }));
  };

  const handleRangeBlur = (parentField, nestedField, fieldLabel) => {
    const range = householdFormData[parentField] || {};
    const start =
      range.start === "" || range.start === null ? 0 : parseFloat(range.start);
    const end =
      range.end === "" || range.end === null ? 0 : parseFloat(range.end);

    if (
      range.start !== "" &&
      range.start !== null &&
      range.end !== "" &&
      range.end !== null &&
      start > end
    ) {
      toast.error(`${fieldLabel}: 'To' can't be less than 'From'.`);
      handleNestedFieldChange(parentField, "end", null);
    }
  };

  const handleDeepRangeBlur = (parentField, nestedField, fieldLabel) => {
    const range = householdFormData[parentField]?.[nestedField] || {};
    const start =
      range.start === "" || range.start === null ? 0 : parseFloat(range.start);
    const end =
      range.end === "" || range.end === null ? 0 : parseFloat(range.end);

    if (
      range.start !== "" &&
      range.start !== null &&
      range.end !== "" &&
      range.end !== null &&
      start > end
    ) {
      toast.error(`${fieldLabel}: 'To' can't be less than 'From'.`);
      handleDeepNestedFieldChange(parentField, nestedField, "end", null);
    }
  };

  const handleSaveHousehold = async () => {
    if (!selectedHousehold) return;

    // Validate data
    const validationErrors = validateHouseholdData(householdFormData);
    if (validationErrors.length > 0) {
      toast.error("Please complete required fields.");
      return;
    }

    setSavingHousehold(true);
    try {
      // Prepare update payload
      const updatePayload = {
        annual_income: householdFormData.annual_income
          ? parseFloat(householdFormData.annual_income)
          : null,
        estimated_total_net_worth: householdFormData.estimated_total_net_worth
          ? parseFloat(householdFormData.estimated_total_net_worth)
          : null,
        estimated_liquid_net_worth: householdFormData.estimated_liquid_net_worth
          ? parseFloat(householdFormData.estimated_liquid_net_worth)
          : null,
        tax_percentage: householdFormData.tax_percentage
          ? parseFloat(householdFormData.tax_percentage)
          : null,
        annual_expense_range:
          householdFormData.annual_expense_range?.start ||
          householdFormData.annual_expense_range?.end
            ? {
                start: householdFormData.annual_expense_range.start
                  ? parseFloat(householdFormData.annual_expense_range.start)
                  : null,
                end: householdFormData.annual_expense_range.end
                  ? parseFloat(householdFormData.annual_expense_range.end)
                  : null,
              }
            : null,
        special_expense_range:
          householdFormData.special_expense_range?.start ||
          householdFormData.special_expense_range?.end
            ? {
                start: householdFormData.special_expense_range.start
                  ? parseFloat(householdFormData.special_expense_range.start)
                  : null,
                end: householdFormData.special_expense_range.end
                  ? parseFloat(householdFormData.special_expense_range.end)
                  : null,
              }
            : null,
        tax_info:
          householdFormData.tax_info?.tax_bracket?.start ||
          householdFormData.tax_info?.tax_bracket?.end
            ? {
                tax_bracket: {
                  start: householdFormData.tax_info.tax_bracket.start
                    ? parseFloat(householdFormData.tax_info.tax_bracket.start)
                    : null,
                  end: householdFormData.tax_info.tax_bracket.end
                    ? parseFloat(householdFormData.tax_info.tax_bracket.end)
                    : null,
                },
              }
            : null,
        investor_associations: householdFormData.investor_associations || null,
        investor_experience: householdFormData.investor_experience || null,
        status: householdFormData.status || "active",
      };

      // Remove null values
      Object.keys(updatePayload).forEach((key) => {
        if (updatePayload[key] === null || updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      const response = await axiosInstance.put(
        `/households/${selectedHousehold.id}`,
        updatePayload,
      );
      setSelectedHousehold(response.data);
      setEditingHousehold(false);
      toast.success("Household updated.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update household."));
    } finally {
      setSavingHousehold(false);
    }
  };

  const handleHouseholdClick = async (householdId) => {
    // Prevent multiple clicks if already loading
    if (householdLoading) {
      return;
    }

    // 1. Fetch small details if we don't have status readily available?
    // We do have status in `households` list usually.
    const household = households.find((h) => h.id === householdId);

    // Check Status for Wizard Trigger
    if (household) {
      const wizardStatuses = [
        "created",
        "general_info_completed",
        "household_setup_initiated",
      ];
      if (wizardStatuses.includes(household.status)) {
        setWizardHouseholdId(householdId);
        setWizardInitialStatus(household.status);
        setShowWizard(true);
        return;
      }
    }

    // Navigate to the new household detail page
    navigate(`/project/${projectId}/house/${householdId}`);
  };

  const handleWizardComplete = () => {
    const householdId = wizardHouseholdId;
    setShowWizard(false);
    setWizardHouseholdId(null);
    setWizardInitialStatus(null);
    setNeedsAttentionRefreshKey((k) => k + 1);

    if (householdId && projectId) {
      navigate(`/project/${projectId}/house/${householdId}`);
    } else {
      fetchHouseholds();
    }
  };

  const handleEditMember = (member) => {
    setEditingMemberId(member.id);
    // Deep clone personal_info to avoid mutating the original
    const personalInfo = member.personal_info
      ? JSON.parse(JSON.stringify(member.personal_info))
      : {};
    if (!personalInfo.country_of_citizenship) {
      personalInfo.country_of_citizenship = "USA";
    }
    // Ensure address is an array
    if (!personalInfo.address || !Array.isArray(personalInfo.address)) {
      personalInfo.address = personalInfo.address ? [personalInfo.address] : [];
    }
    // Deep clone employment_info
    const employmentInfo = member.employment_info
      ? JSON.parse(JSON.stringify(member.employment_info))
      : {};
    // Ensure employment address exists if employment_info exists
    if (Object.keys(employmentInfo).length > 0 && !employmentInfo.address) {
      employmentInfo.address = {};
    }
    setMemberFormData({
      type: member.type || "",
      is_primary: member.is_primary || false,
      personal_info: personalInfo,
      id_info: member.id_info ? JSON.parse(JSON.stringify(member.id_info)) : [],
      employment_info: employmentInfo,
      status: member.status || "active",
    });
  };

  const handleSaveMember = async (memberId) => {
    const dobError = validateDob(memberFormData.personal_info?.dob);
    if (dobError) {
      toast.error(dobError);
      return;
    }
    setSavingMember(true);
    try {
      const updatePayload = {
        type: memberFormData.type || null,
        is_primary: memberFormData.is_primary || false,
        personal_info: memberFormData.personal_info || null,
        id_info: memberFormData.id_info || null,
        employment_info: memberFormData.employment_info || null,
        status: memberFormData.status || "active",
      };

      // Remove null values
      Object.keys(updatePayload).forEach((key) => {
        if (updatePayload[key] === null || updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      const response = await axiosInstance.put(
        `/households/members/${memberId}`,
        updatePayload,
      );

      // Update the member in selectedHousehold
      setSelectedHousehold((prev) => ({
        ...prev,
        members: prev.members.map((m) =>
          m.id === memberId ? response.data : m,
        ),
      }));

      setEditingMemberId(null);
      toast.success("Member updated.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update member."));
    } finally {
      setSavingMember(false);
    }
  };

  const handleEditAccount = (account) => {
    setEditingAccountId(account.id);
    setAccountFormData({
      old_account_number: account.transfer_info?.old_account_number || "",
      new_account_number: account.new_account_number || "",
      custodian: account.custodian || "",
      registration: account.registration || "",
      type: account.type || "",
      fee_billed_percentage: account.fee_billed_percentage ?? "",
      account_value: account.account_value ?? "",
      new_rep_id: account.new_rep_id || "",
      proceeds: account.proceeds || "",
      security_purchases: account.security_purchases || "",
      distribution_income: account.distribution_income || "",
      margin: account.margin || false,
      options: account.options || false,
      fee_based_account: account.fee_based_account || false,
      check_writing: account.check_writing || false,
      owners_info: account.owners_info || [],
      transfer_info: account.transfer_info || {},
      beneficiaries: account.beneficiaries || [],
      trusted_contacts: account.trusted_contacts || [],
      risk_tolerance: account.risk_tolerance || {},
      withdrawals: account.withdrawals || [],
      deposits: account.deposits || [],
      trust_info: account.trust_info || null,
    });
  };

  const handleSaveAccount = async (accountId) => {
    // Validate account data
    const validationErrors = validateAccountData(accountFormData);
    if (validationErrors.length > 0) {
      toast.error("Please complete required fields.");
      return;
    }

    setSavingAccount(true);
    try {
      const updatePayload = {
        new_account_number: accountFormData.new_account_number || null,
        custodian: accountFormData.custodian || null,
        registration: accountFormData.registration || null,
        type: accountFormData.type || null,
        fee_billed_percentage:
          accountFormData.fee_billed_percentage === "" ||
          accountFormData.fee_billed_percentage == null
            ? null
            : parseFloat(accountFormData.fee_billed_percentage),
        account_value:
          accountFormData.account_value === "" ||
          accountFormData.account_value == null
            ? null
            : parseFloat(accountFormData.account_value),
        new_rep_id: accountFormData.new_rep_id || null,
        proceeds: accountFormData.proceeds
          ? parseFloat(accountFormData.proceeds)
          : null,
        security_purchases: accountFormData.security_purchases
          ? parseFloat(accountFormData.security_purchases)
          : null,
        distribution_income: accountFormData.distribution_income
          ? parseFloat(accountFormData.distribution_income)
          : null,
        margin: accountFormData.margin || false,
        options: accountFormData.options || false,
        fee_based_account: accountFormData.fee_based_account || false,
        check_writing: accountFormData.check_writing || false,
        owners_info: accountFormData.owners_info || null,
        transfer_info: {
          ...(accountFormData.transfer_info || {}),
          old_account_number: accountFormData.old_account_number || null,
        },
        beneficiaries: accountFormData.beneficiaries || null,
        trusted_contacts: accountFormData.trusted_contacts || null,
        risk_tolerance: accountFormData.risk_tolerance || null,
        withdrawals: accountFormData.withdrawals || null,
        deposits: accountFormData.deposits || null,
        trust_info: accountFormData.trust_info || null,
      };

      // Remove null values
      Object.keys(updatePayload).forEach((key) => {
        if (updatePayload[key] === null || updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      const response = await axiosInstance.put(
        `/households/accounts/${accountId}`,
        updatePayload,
      );

      // Update the account in selectedHousehold
      setSelectedHousehold((prev) => ({
        ...prev,
        accounts: prev.accounts.map((a) =>
          a.id === accountId ? response.data : a,
        ),
      }));

      // Also update selectedAccount if it's the same one (for modal view)
      if (selectedAccount && selectedAccount.id === accountId) {
        setSelectedAccount(response.data);
      }

      setEditingAccountId(null);
      setAccountFormData({});
      toast.success("Account updated.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't update account."));
    } finally {
      setSavingAccount(false);
    }
  };

  const getUploaderEmail = () => {
    if (user?.email) {
      return user.email;
    }
    try {
      const storedUser = JSON.parse(localStorage.getItem("userData") || "{}");
      return storedUser.email || storedUser.username || "";
    } catch (err) {
      console.error("Error parsing stored user data:", err);
      return "";
    }
  };
  const currentUserEmail = getUploaderEmail().trim().toLowerCase();
  const canAccessPrivilegedProjectActions =
    currentUserEmail &&
    PROJECT_DETAIL_PRIVILEGED_EMAILS.includes(currentUserEmail);

  const generateBatchId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const handleFilesUpload = async (filesList) => {
    const files = Array.from(filesList || []).filter(Boolean);
    if (!files.length) return;

    // For bulk upload, we'll process one file at a time (each Excel can have multiple households)
    if (files.length > 1) {
      toast.error("Upload one Excel file at a time.");
      return;
    }

    const invalidFiles = files.filter(
      (file) =>
        !file.name.toLowerCase().endsWith(".xlsx") &&
        !file.name.toLowerCase().endsWith(".xls"),
    );
    if (invalidFiles.length > 0) {
      toast.error("Only Excel files (.xlsx, .xls) are allowed.");
      return;
    }

    const bulkFile = files[0];
    if (fileExceedsMaxUploadSize(bulkFile)) {
      toast.error(`File is too large (max ${MAX_UPLOAD_FILE_SIZE_LABEL}).`);
      return;
    }

    if (!selectedUploadAdvisorId) {
      toast.error("Please select an advisor.");
      return;
    }

    setUploading(true);

    try {
      // Upload directly to the bulk upload endpoint
      const file = bulkFile;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("advisor_id", selectedUploadAdvisorId);

      const response = await axiosInstance.post(
        `/households/project/${projectId}/bulk-upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { message, created_households, skipped_households } = response.data;

      await fetchHouseholds();
      setSelectedUploadAdvisorId("");

      const createdCount = created_households?.length || 0;
      const skippedCount = skipped_households?.length || 0;
      if (createdCount && skippedCount) {
        toast.success(`Created ${createdCount}, skipped ${skippedCount}.`);
      } else if (createdCount) {
        toast.success(`Created ${createdCount} household${createdCount === 1 ? "" : "s"}.`);
      } else {
        toast.info(message || "Upload processed.");
      }
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't upload documents."));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFilesUpload(files);
    }
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // Generate UUID for batch ID
  const generateUUID = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback: Generate UUID v4 manually
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
  };

  // Handlers for Enrich Existing Households Modal
  const handleEnrichHouseholdFilesUpload = async (filesList) => {
    const files = Array.from(filesList || []).filter(Boolean);
    if (!files.length) return;
    if (files.length > 1) {
      toast.error("Upload one file at a time.");
      return;
    }

    const invalidFiles = files.filter(
      (file) =>
        !file.name.toLowerCase().endsWith(".xlsx") &&
        !file.name.toLowerCase().endsWith(".xls"),
    );
    if (invalidFiles.length > 0) {
      toast.error("Only Excel files (.xlsx, .xls) are allowed.");
      return;
    }

    const file = files[0];
    if (fileExceedsMaxUploadSize(file)) {
      toast.error(`File is too large (max ${MAX_UPLOAD_FILE_SIZE_LABEL}).`);
      return;
    }

    if (!enrichHouseholdAdvisorId) {
      toast.error("Please select an advisor.");
      return;
    }

    setEnrichHouseholdUploading(true);

    try {
      const batchId = generateUUID();

      // Step 1: Get presigned URL
      const presignedResponse = await axiosInstance.post(
        `/api/files/presigned-urls`,
        {
          file_names: [file.name],
          batch_id: batchId,
          project_id: projectId,
          advisor_id: enrichHouseholdAdvisorId,
          upload_type: "enrich_households_upload",
        },
      );

      const { presigned_urls } = presignedResponse.data;
      if (!presigned_urls || presigned_urls.length === 0) {
        throw new Error("No presigned URL received from server");
      }

      const presignedUrlData = presigned_urls[0];

      // Step 2: Upload file to presigned URL
      const contentType = file.name.toLowerCase().endsWith(".xlsx")
        ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        : "application/vnd.ms-excel";

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            const percentComplete = (e.loaded / e.total) * 100;
            console.log(`Upload progress: ${percentComplete.toFixed(2)}%`);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status === 200 || xhr.status === 204) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.open("PUT", presignedUrlData.presigned_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
      });

      // Step 3: Confirm upload
      const confirmResponse = await axiosInstance.post(
        `/api/files/confirm-upload`,
        {
          project_id: projectId,
          batch_id: batchId,
          upload_type: "enrich_households_upload",
        },
      );

      setEnrichHouseholdAdvisorId("");
      setShowEnrichHouseholdModal(false);
      toast.info(confirmResponse.data?.message || "Upload is processing in the background.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't upload documents."));
    } finally {
      setEnrichHouseholdUploading(false);
    }
  };

  const handleEnrichHouseholdFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleEnrichHouseholdFilesUpload(files);
    }
    e.target.value = "";
  };

  const handleEnrichHouseholdDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEnrichHouseholdDragging(true);
  };

  const handleEnrichHouseholdDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEnrichHouseholdDragging(false);
  };

  const handleEnrichHouseholdDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setEnrichHouseholdDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleEnrichHouseholdFilesUpload(files);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFilesUpload(files);
    }
  };

  // Helper function to check if there's a primary member with valid email
  const hasPrimaryMemberWithEmail = () => {
    if (!selectedHousehold?.members || selectedHousehold.members.length === 0) {
      return false;
    }

    const primaryMember = selectedHousehold.members.find(
      (member) => member.is_primary === true,
    );
    if (!primaryMember || !primaryMember.personal_info) {
      return false;
    }

    // Handle personal_info as object or JSON string
    let personalInfo = primaryMember.personal_info;
    if (typeof personalInfo === "string") {
      try {
        personalInfo = JSON.parse(personalInfo);
      } catch {
        return false;
      }
    }

    // Check if email exists and is not empty or null
    const email = personalInfo?.email;
    return email && email.trim() !== "";
  };

  const handleSendToClient = async () => {
    if (!selectedHousehold || !selectedHousehold.id) {
      setSendToClientErrorMessage("Please select a household first.");
      setShowSendToClientError(true);
      return;
    }

    setSendingToClient(true);
    setShowSendToClientSuccess(false);
    setShowSendToClientError(false);
    setSendToClientEmail("");
    setSendToClientErrorMessage("");

    try {
      const response = await axiosInstance.post(
        `/households/${selectedHousehold.id}/send-to-client`,
      );

      // Only show success modal for 2xx responses
      if (response.status >= 200 && response.status < 300) {
        const { email } = response.data;
        setSendToClientEmail(email || "");
        setShowSendToClientSuccess(true);
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.error("Error sending email to client:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to send email to client. Please try again.";
      setSendToClientErrorMessage(errorMessage);
      setShowSendToClientError(true);
    } finally {
      setSendingToClient(false);
    }
  };

  const getFormDisplayName = (formType) => {
    const formDisplayNames = {
      iws_ira: "Retirement Application",
      iws_application: "Individual/Joint Application",
      iws_tod: "TOD",
      iws_toa: "TOA",
      iws_trust_application: "Trust Application",
      iws_trust_cert: "Trust Certificate",
      iws_one_time_distribution: "One Time Distribution",
      iws_periodic_distribution: "Periodic Distribution",
      iws_spi: "Standing Payment Instructions",
      iws_margin: "Margin Application",
      iws_option: "Options Application",
      nfs_toa: "TOA",
      nfs_ira: "Retirement Application",
      nfs_brokerage: "Trust Application",
      nfs_trust_cert: "Trust Certificate",
      nfs_indi_joint_application: "Individual/Joint Application",
      nfs_retirement_spi: "SPI - Retirement",
      nfs_non_retirement_spi: "SPI - Non Retirement",
      nfs_periodic_distribution: "Periodic Distribution",
      nfs_margin: "Margin Application",
      nfs_option: "Options Application",
    };
    return formDisplayNames[formType] || formType;
  };

  // Ordered form arrays for display
  const iwsFormOrder = [
    "iws_application", // Individual/ Joint Application
    "iws_ira", // Retirement Application
    "iws_trust_application", // Trust Application
    "iws_tod", // TOD
    "iws_trust_cert", // Trust Certificate
    "iws_toa", // TOA
    "iws_spi", // Standing Payment Instructions
    "iws_periodic_distribution", // Periodic Distribution
    "iws_one_time_distribution", // One Time Distribution
    "iws_margin", // Margin Application
    "iws_option", // Options Application
  ];

  const nfsFormOrder = [
    "nfs_indi_joint_application", // Individual/ Joint Application
    "nfs_ira", // Retirement Application
    "nfs_brokerage", // Trust Application
    "nfs_trust_cert", // Trust Certificate
    "nfs_toa", // TOA
    "nfs_non_retirement_spi", // SPI - Non Retirement
    "nfs_retirement_spi", // SPI - Retirement
    "nfs_periodic_distribution", // Periodic Distribution
    "nfs_margin", // Margin Application
    "nfs_option", // Options Application
  ];

  const formatTimeHorizon = (timeHorizon) => {
    if (!timeHorizon || typeof timeHorizon !== "object") {
      return timeHorizon || "N/A";
    }

    const start = timeHorizon.start ?? 0;
    const end = timeHorizon.end ?? 0;

    // If end is 100, show as "start+"
    if (end === 100) {
      return `${start}+ years`;
    }

    // Otherwise show as range
    return `${start} - ${end} years`;
  };

  const formatLiquidityAmount = (amount) => {
    if (!amount || typeof amount !== "object") {
      return amount || "N/A";
    }

    const start = amount.start ?? 0;
    const end = amount.end ?? 0;

    return `${start.toLocaleString()} - ${end.toLocaleString()} years`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    // Handle yyyy-mm-dd format
    const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      const [, year, month, day] = dateMatch;
      return `${month}-${day}-${year}`;
    }

    return dateString;
  };

  const handleDeleteProject = async () => {
    if (!project || deleteConfirmationText !== project.title) {
      return;
    }

    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/projects/${projectId}`);
      toast.success("Project deleted.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't delete project."));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmationText("");
    }
  };

  const fetchAccountForms = (accountId) => {
    // Check if forms data already exists for this account
    if (!accountFormsData[accountId]) {
      toast.info("Forms are loading. Try again in a moment.");
      return;
    }

    // Show the form selector with existing data
    setShowFormTypeSelectorForAccount(accountId);
  };

  const handleFormToggle = (accountId, formType) => {
    setSelectedFormsForAccount((prev) => ({
      ...prev,
      [accountId]: {
        ...(prev[accountId] || {}),
        [formType]: !(prev[accountId]?.[formType] || false),
      },
    }));
  };

  const handleBulkDownloadForms = async (accountId) => {
    if (!selectedHousehold) {
      toast.error("Select a household first.");
      return;
    }

    const selectedForms = Object.keys(
      selectedFormsForAccount[accountId] || {},
    ).filter((formType) => selectedFormsForAccount[accountId][formType]);

    if (selectedForms.length === 0) {
      toast.error("Select at least one form.");
      return;
    }

    const accountFormsInfo = accountFormsData[accountId];
    if (!accountFormsInfo) {
      toast.error("Account forms not available.");
      return;
    }

    const isIWS = accountFormsInfo.type === "iws";
    const requestBody = {};
    if (isIWS) {
      iwsFormOrder.forEach((formType) => {
        requestBody[formType] = selectedForms.includes(formType);
      });
    } else {
      nfsFormOrder.forEach((formType) => {
        requestBody[formType] = selectedForms.includes(formType);
      });
    }

    setDownloadingAccountId(accountId);
    try {
      const response = await axiosInstance.post(
        `/forms/project/${projectId}/households/${selectedHousehold.id}/accounts/${accountId}/fill-forms`,
        requestBody,
      );

      const { presigned_url, zip_filename } = response.data;
      if (!presigned_url) {
        throw new Error("No presigned URL received from server");
      }

      const downloadResponse = await fetch(presigned_url);
      if (!downloadResponse.ok) {
        throw new Error("Failed to download zip file");
      }

      const blob = await downloadResponse.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;

      link.setAttribute(
        "download",
        zip_filename || `account_${accountId}_forms.zip`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setShowFormTypeSelectorForAccount(null);
      setSelectedFormsForAccount((prev) => {
        const newState = { ...prev };
        delete newState[accountId];
        return newState;
      });
      setAccountFormsData((prev) => {
        const newState = { ...prev };
        delete newState[accountId];
        return newState;
      });

      toast.success("Forms downloaded.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't download forms."));
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handleDownloadHouseholdForms = async () => {
    if (!selectedHousehold) {
      toast.error("Select a household first.");
      return;
    }

    setDownloadingHouseholdForms(true);
    try {
      const response = await axiosInstance.get(
        `/households/project/${projectId}/households/${selectedHousehold.id}/download-household-forms`,
        {
          responseType: "blob",
        },
      );

      // Will be 'application/pdf' for single PDF or 'application/zip' for multiple PDFs
      const contentType = response.headers["content-type"] || "application/pdf";

      // Get filename from Content-Disposition header (API always sets this with correct extension)
      let filename = `household_${selectedHousehold.name}_forms.pdf`; // fallback
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "");
        }
      }

      // Create blob with the content type from the API response
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("Firm docs downloaded.");
    } catch (error) {
      toast.error(humanizeError(error, "Couldn't download firm docs."));
    } finally {
      setDownloadingHouseholdForms(false);
    }
  };

  // Removed handleGeneratePDF - documents section removed

  // Show skeleton loader instead of blocking - makes UI feel faster
  if (loading && !project) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
        <Header user={user} />
        <div className="flex flex-1">
          <Sidebar />
          <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10">
            <div className="max-w-7xl mx-auto w-full">
              {/* Skeleton Project Header */}
              <div className="mb-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
                <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                  <div className="h-6 w-32 bg-gray-200 rounded-lg mb-6 animate-pulse"></div>
                  <div className="h-10 w-3/4 bg-gray-200 rounded-lg mb-3 animate-pulse"></div>
                  <div className="h-6 w-1/2 bg-gray-200 rounded-lg animate-pulse"></div>
                </div>
              </div>
              {/* Skeleton Households Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="card bg-gradient-to-br from-white via-blue-50/20 to-white border-blue-200/50 shadow-lg">
                  <div className="h-16 bg-gray-200 rounded-lg mb-6 animate-pulse"></div>
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="p-4 rounded-xl border-2 border-gray-200 animate-pulse"
                      >
                        <div className="h-6 w-2/3 bg-gray-200 rounded mb-3"></div>
                        <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="card bg-gradient-to-br from-white via-emerald-50/20 to-white border-emerald-200/50 shadow-lg">
                  <div className="h-16 bg-gray-200 rounded-lg mb-6 animate-pulse"></div>
                  <div className="h-48 bg-gray-100 rounded-xl animate-pulse"></div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-emerald-50/30 via-white to-blue-50/20">
      <Header user={user} />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 p-8 bg-gradient-to-b from-transparent to-emerald-50/10">
          <div className="max-w-7xl mx-auto w-full">
            {/* Project Header Section */}
            <div className="mb-8 relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-transparent to-blue-500/5 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 border border-emerald-100/50 shadow-xl">
                <button
                  onClick={() => navigate("/dashboard")}
                  className="mb-6 flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-semibold transition-all hover:gap-3 group"
                >
                  <ChevronLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                  Back to Dashboard
                </button>
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex-1">
                    <h1 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-gray-900 bg-clip-text text-transparent mb-2">
                      {project?.title}
                    </h1>
                    {project?.description && (
                      <p className="text-gray-600 mt-3 text-lg font-medium">
                        {project.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Progress Bar */}
                    {project?.total_households > 0 && (
                      <div className="flex flex-col items-end">
                        <div className="flex items-center justify-between w-64 mb-1">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            Progress
                          </span>
                          <span className="text-xs font-bold text-emerald-600">
                            {Math.floor(project.completion_percentage || 0)}%
                          </span>
                        </div>
                        <div className="w-64 h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="bg-emerald-500 h-full rounded-full transition-all duration-700 ease-out shadow-sm"
                            style={{
                              width: `${project.completion_percentage || 0}%`,
                            }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium">
                          {project.completed_households || 0} of{" "}
                          {project.total_households} Households
                        </div>
                      </div>
                    )}
                    {project?.status && (
                      <span
                        className={`badge px-3 py-1 font-medium ${
                          project.status === "completed"
                            ? "badge-success"
                            : project.status === "initiated" ||
                                project.status === "created"
                              ? "badge-info"
                              : "badge-warning"
                        }`}
                      >
                        {project.status === "created"
                          ? "Ongoing"
                          : project.status.charAt(0).toUpperCase() +
                            project.status.slice(1)}
                      </span>
                    )}
                    {/* Delete Project Button - Commented out */}
                    {/* <button
                    onClick={() => setShowDeleteModal(true)}
                    className="text-sm px-4 py-2 flex items-center gap-2 bg-red-50 border border-red-600 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Project
                  </button> */}
                  </div>
                </div>
                {/* Custodian and Advisors Section */}
                <div className="mt-4 pt-4 border-t border-emerald-200/50">
                  <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
                    {/* Left Section (70%) - Custodian, Destination Firm and Project Advisors */}
                    <div className="lg:col-span-7">
                      <div className="flex items-center gap-6 flex-wrap">
                        <div className="flex items-center gap-3">
                          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Destination Custodian
                          </h3>
                          <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                            {project?.custodian || "Fidelity"}
                          </div>
                        </div>
                        <div className="h-4 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-3">
                          <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Destination Firm
                          </h3>
                          <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700">
                            {project?.destination_firm || "N/A"}
                          </div>
                        </div>
                        {project?.target_end_date && (
                          <>
                            <div className="h-4 w-px bg-gray-300"></div>
                            <div className="flex items-center gap-3">
                              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                                Target End Date
                              </h3>
                              <div className="px-3 py-2 rounded-lg text-sm font-semibold bg-amber-50 text-amber-700">
                                {formatDateForDisplay(project.target_end_date)}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      {project?.advisors && project.advisors.length > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center gap-3 overflow-x-auto">
                            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex-shrink-0">
                              Project Advisors
                            </h3>
                            <div className="h-4 w-px bg-gray-300 mx-2"></div>
                            <div className="flex items-center gap-2 flex-nowrap">
                              {project.advisors.map((advisor, index) => {
                                const advisorKey =
                                  advisor.id ||
                                  advisor.g_number ||
                                  `advisor-${index}`;

                                return (
                                  <div
                                    key={advisorKey}
                                    className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-50 text-emerald-700 flex-shrink-0"
                                  >
                                    {advisor.first_name}{" "}
                                    {advisor.middle_name
                                      ? `${advisor.middle_name} `
                                      : ""}
                                    {advisor.last_name}
                                    {advisor.g_number &&
                                      ` (G#${advisor.g_number})`}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                  {/* Right Section (30%) - Action Buttons */}
                  <div className="lg:col-span-3 flex flex-col gap-3 justify-center">
                    <button
                      onClick={() => setShowCreateHouseholdModal(true)}
                      className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Create New Households
                    </button>
                    {canAccessPrivilegedProjectActions && (
                      <>
                        <button
                          onClick={() => setShowEnrichHouseholdModal(true)}
                          className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <Upload className="w-4 h-4" />
                          Enrich Existing Households
                        </button>
                        <button
                          onClick={() => setShowExportProjectDataModal(true)}
                          className="w-full px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <FileDown className="w-4 h-4" />
                          Export Project Data
                        </button>
                      </>
                    )}
                    {/* <button
                      onClick={() => navigate(`/project/${projectId}/transition-tracker`)}
                      className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      Transition Tracker
                    </button> */}
                      {canAccessPrivilegedProjectActions && (
                        <button
                          onClick={() =>
                            navigate(`/project/${projectId}/insights`)
                          }
                          className="w-full px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                        >
                          <PieChart className="w-4 h-4" />
                          Project Insights
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Split Layout: Left - Households Added, Right - Upload */}
            {false && !showDetailedView && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Left Side - Households Added */}
                <div className="card bg-gradient-to-br from-white via-blue-50/20 to-white border-blue-200/50 shadow-lg">
                  <div className="flex items-center justify-between mb-6 pb-5 border-b border-blue-200/50">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <Home className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-blue-700 bg-clip-text text-transparent">
                          Households Added
                        </h2>
                        <p className="text-xs text-gray-500 mt-1">
                          Last {Math.min(5, households.length)} created
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {households.length > 5 && (
                        <button
                          onClick={() =>
                            setExpandedHouseholds(!expandedHouseholds)
                          }
                          className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-sm font-semibold text-blue-700"
                        >
                          {expandedHouseholds ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              Expand All
                            </>
                          )}
                        </button>
                      )}
                      {households.length > 0 && (
                        <button
                          onClick={() => {
                            setShowDetailedView(!showDetailedView);
                            setDetailedViewPage(1);
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors text-sm font-semibold text-emerald-700"
                        >
                          <Menu className="w-4 h-4" />
                          {showDetailedView
                            ? "Hide Detailed View"
                            : "View All Households"}
                        </button>
                      )}
                    </div>
                  </div>
                  {householdsLoading ? (
                    <div className="text-center py-12">
                      <div className="flex flex-col items-center justify-center">
                        <Loader2 className="animate-spin h-10 w-10 text-blue-500 mb-4" />
                        <p className="text-gray-600 font-medium text-sm">
                          Loading households...
                        </p>
                      </div>
                    </div>
                  ) : households.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                        <Home className="w-8 h-8 text-blue-500" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        No households created yet
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Upload Household Excel file to create households
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {(expandedHouseholds
                        ? households
                        : households.slice(0, 5)
                      ).map((household) => (
                        <div
                          key={household.id}
                          className="p-4 rounded-xl border-2 border-gray-200 hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50/50 hover:to-emerald-50/30 cursor-pointer transition-all duration-300 group shadow-sm hover:shadow-md"
                          onClick={() => handleHouseholdClick(household.id)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors mb-2 truncate">
                                {household.name}
                              </h3>
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-lg">
                                  <Users className="w-3.5 h-3.5 text-blue-600" />
                                  <span className="text-xs font-semibold text-blue-700">
                                    {household.member_count || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 rounded-lg">
                                  <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                                  <span className="text-xs font-semibold text-emerald-700">
                                    {household.account_count || 0}
                                  </span>
                                </div>
                                {household.completion_percentage !==
                                  undefined && (
                                  <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded-lg">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-purple-600" />
                                    <span className="text-xs font-semibold text-purple-700">
                                      {Math.floor(
                                        household.completion_percentage,
                                      )}
                                      %
                                    </span>
                                  </div>
                                )}
                                <StatusBadge
                                  status={household.status}
                                  subStatus={household.sub_status}
                                  staleHousehold={!!household.stale_household}
                                />
                              </div>
                              <div className="space-y-1 mb-2">
                                {household.owner_first_name && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <User className="w-3.5 h-3.5 text-purple-600" />
                                    <span className="font-medium">
                                      Uploaded by: {household.owner_first_name}{" "}
                                      {household.owner_middle_name
                                        ? `${household.owner_middle_name} `
                                        : ""}
                                      {household.owner_last_name}
                                      {household.owner_g_number &&
                                        ` (G#${household.owner_g_number})`}
                                    </span>
                                  </div>
                                )}
                                {household.advisor_first_name && (
                                  <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <Users className="w-3.5 h-3.5 text-blue-600" />
                                    <span className="font-medium">
                                      Advisor: {household.advisor_first_name}{" "}
                                      {household.advisor_last_name}
                                      {household.advisor_g_number &&
                                        ` (G#${household.advisor_g_number})`}
                                    </span>
                                  </div>
                                )}
                              </div>
                              {household.excel_upload_date && (
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <Calendar className="w-3.5 h-3.5" />
                                  <span>
                                    {formatDateForDisplay(household.excel_upload_date)}
                                  </span>
                                </div>
                              )}
                              {household.excel_file_path && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const response = await axiosInstance.get(
                                        `/households/${household.id}/download-excel`,
                                        {
                                          responseType: "blob",
                                        },
                                      );
                                      const url = window.URL.createObjectURL(
                                        new Blob([response.data]),
                                      );
                                      const link = document.createElement("a");
                                      link.href = url;
                                      link.setAttribute(
                                        "download",
                                        `household_${household.name}_excel.xlsx`,
                                      );
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                    } catch (error) {
                                      toast.error(humanizeError(error, "Couldn't download Excel file."));
                                    }
                                  }}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors text-xs font-semibold text-emerald-700 mt-2"
                                  title="Download Excel"
                                >
                                  <FileDown className="w-3.5 h-3.5" />
                                  Download Excel
                                </button>
                              )}
                            </div>
                            <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center group-hover:from-blue-500 group-hover:to-emerald-500 transition-all duration-300 ml-2">
                              <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Right Side - Upload */}
                <div className="card bg-gradient-to-br from-white via-emerald-50/20 to-white border-emerald-200/50 shadow-lg">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <UploadCloud className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-emerald-700 bg-clip-text text-transparent">
                      Upload Household Data
                    </h2>
                  </div>

                  {/* Advisor Selection */}
                  {project?.advisors && project.advisors.length > 0 && (
                    <div className="mb-4">
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Select Advisor for this Document *
                      </label>
                      <select
                        value={selectedUploadAdvisorId}
                        onChange={(e) =>
                          setSelectedUploadAdvisorId(e.target.value)
                        }
                        required
                        className="input-modern text-base w-full"
                      >
                        <option value="">-- Select Advisor --</option>
                        {project.advisors.map((advisor, index) => (
                          <option
                            key={index}
                            value={advisor.user_id || String(index)}
                          >
                            {advisor.first_name} {advisor.last_name}{" "}
                            {advisor.g_number && `(G#${advisor.g_number})`}{" "}
                            {advisor.rr_code && `(RR:${advisor.rr_code})`}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <label className="block">
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`mt-2 flex justify-center px-4 pt-6 pb-6 border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer ${
                        isDragging
                          ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100/50 shadow-lg scale-[1.02]"
                          : "border-emerald-200 hover:border-emerald-400 bg-gradient-to-br from-gray-50/50 to-emerald-50/30 hover:shadow-md"
                      }`}
                    >
                      <div className="space-y-2 text-center">
                        <div className="mx-auto w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                          <svg
                            className="h-6 w-6 text-white"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="flex text-sm text-gray-700 font-medium justify-center items-center gap-2">
                          <span className="relative cursor-pointer font-bold text-emerald-600 hover:text-emerald-700 transition-colors">
                            Upload files
                          </span>
                          <span className="text-gray-500">or</span>
                          <span className="text-gray-600">drag and drop</span>
                        </div>
                        <p className="text-xs text-gray-500 font-medium">
                          Excel files (.xlsx, .xls), max{" "}
                          {MAX_UPLOAD_FILE_SIZE_LABEL} MB per file — up to{" "}
                          {MAX_BULK_UPLOAD_FILES} files per batch
                        </p>
                      </div>
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileInputChange}
                        disabled={uploading}
                        multiple
                        className="hidden"
                      />
                    </div>
                  </label>
                  {uploading && (
                    <div className="mt-4 flex items-center gap-2 text-emerald-600">
                      <Loader2 className="animate-spin h-5 w-5" />
                      <span className="font-medium text-sm">
                        Uploading files and starting processing...
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {showDetailedView && (
              <>
              {(() => {
                const m = project?.project_metrics || {};
                const total = m.total_households ?? project?.total_households ?? 0;
                const dataCollectionCount = m.data_collection?.count ?? 0;
                const dataCollectionAum = m.data_collection?.aum_dollars ?? 0;
                const repaperingCount =
                  m.repapering?.count ??
                  ((project?.docusign_initiated_households || 0) +
                    (project?.pending_for_execution_households || 0));
                const repaperingAum = m.repapering?.aum_dollars ?? 0;
                const completedCount =
                  m.completed?.count ?? (project?.completed_households || 0);
                const completedAum = m.completed?.aum_transferred_dollars ?? 0;
                const targetEndDate =
                  m.target_end_date || project?.target_end_date || project?.end_date || null;

                const pct = (num, denom) =>
                  !denom || denom <= 0 ? 0 : Math.min(100, Math.round((num / denom) * 100));
                const fmtAum = (n) => {
                  if (n == null || isNaN(n)) return "—";
                  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
                  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
                  return `$${Number(n).toFixed(0)}`;
                };
                const daysToTarget = (() => {
                  if (!targetEndDate) return null;
                  const t = new Date(targetEndDate);
                  if (isNaN(t.getTime())) return null;
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  t.setHours(0, 0, 0, 0);
                  return Math.round((t - today) / (1000 * 60 * 60 * 24));
                })();
                const fmtTargetDate = formatDateForDisplay(targetEndDate);
                const daysOverdue = daysToTarget != null && daysToTarget < 0;

                const PhaseCard = ({
                  label,
                  count,
                  total: t,
                  caption,
                  accent,
                  icon,
                }) => (
                  <div
                    className={`bg-white p-4 rounded-2xl border ${accent.border} shadow-sm hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                        {label}
                      </p>
                      <div
                        className={`w-9 h-9 ${accent.iconBg} rounded-xl flex items-center justify-center ${accent.iconText}`}
                      >
                        {icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-extrabold text-gray-900 mb-2">
                      {count}
                      <span className="text-base font-semibold text-gray-400">
                        {" "}of {t}
                      </span>
                    </h3>
                    <div className={`h-1.5 w-full ${accent.barTrack} rounded-full overflow-hidden mb-2`}>
                      <div
                        className={`h-full ${accent.barFill} rounded-full transition-[width] duration-500`}
                        style={{ width: `${pct(count, t)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500">{caption}</p>
                  </div>
                );

                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <PhaseCard
                      label="Data Collection Phase"
                      count={dataCollectionCount}
                      total={total}
                      caption={`${fmtAum(dataCollectionAum)} AUM`}
                      accent={{
                        border: "border-blue-100",
                        iconBg: "bg-blue-50",
                        iconText: "text-blue-600",
                        barTrack: "bg-blue-100",
                        barFill: "bg-blue-600",
                      }}
                      icon={
                        <ClipboardCheck className="w-5 h-5" />
                      }
                    />
                    <PhaseCard
                      label="Repapering Phase"
                      count={repaperingCount}
                      total={total}
                      caption={`${fmtAum(repaperingAum)} AUM`}
                      accent={{
                        border: "border-amber-100",
                        iconBg: "bg-amber-50",
                        iconText: "text-amber-600",
                        barTrack: "bg-amber-100",
                        barFill: "bg-amber-500",
                      }}
                      icon={
                        <Pencil className="w-5 h-5" />
                      }
                    />
                    <PhaseCard
                      label="Completed"
                      count={completedCount}
                      total={total}
                      caption={`${fmtAum(completedAum)} AUM`}
                      accent={{
                        border: "border-emerald-100",
                        iconBg: "bg-emerald-50",
                        iconText: "text-emerald-600",
                        barTrack: "bg-emerald-100",
                        barFill: "bg-emerald-600",
                      }}
                      icon={
                        <CheckCircle2 className="w-5 h-5" />
                      }
                    />
                    {/* Days to Target End Date — no progress bar; a date metric */}
                    <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                          Days to Target End Date
                        </p>
                        <div
                          className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                            daysOverdue
                              ? "bg-red-50 text-red-600"
                              : "bg-slate-50 text-slate-600"
                          }`}
                        >
                          <Calendar className="w-5 h-5" />
                        </div>
                      </div>
                      <h3
                        className={`text-2xl font-extrabold mb-2 ${
                          daysOverdue ? "text-red-600" : "text-gray-900"
                        }`}
                      >
                        {daysToTarget == null
                          ? "—"
                          : daysOverdue
                            ? `${Math.abs(daysToTarget)} days overdue`
                            : `${daysToTarget} days`}
                      </h3>
                    </div>
                  </div>
                );
              })()}
              </>
            )}

            <ExcelProcessingTracker
              projectId={projectId}
              triggerToken={excelTriggerToken}
            />
            {/* Dashboard tabs: Needs Attention / All Households */}
            {showDetailedView && (
              <div className="mb-4 flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
                <button
                  type="button"
                  onClick={() => {
                    setDashboardTab("needs_attention");
                    persistViewSnapshot("needs_attention", appliedFilters);
                  }}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    dashboardTab === "needs_attention"
                      ? "bg-slate-700 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  Needs Attention
                  {needsAttentionTotal != null && needsAttentionTotal > 0 && (
                    <span
                      className={`ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full ${
                        dashboardTab === "needs_attention"
                          ? "bg-white/20 text-white"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {needsAttentionTotal}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDashboardTab("all_households");
                    persistViewSnapshot("all_households", appliedFilters);
                  }}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                    dashboardTab === "all_households"
                      ? "bg-slate-700 text-white shadow-sm"
                      : "text-gray-600 hover:text-gray-800"
                  }`}
                >
                  All Households
                </button>
              </div>
            )}

            {showDetailedView && dashboardTab === "needs_attention" && (
              <div className="card bg-white border-gray-200/50 shadow-lg p-5">
                <NeedsAttentionView
                  projectId={projectId}
                  refreshKey={needsAttentionRefreshKey}
                  onTotalsLoaded={(total) => setNeedsAttentionTotal(total)}
                  actions={{
                    // "Kick off" — start the mandatory advisor flow in-page.
                    onKickOff: (card) => {
                      setWizardHouseholdId(card.id);
                      setWizardInitialStatus(card.status);
                      setShowWizard(true);
                    },
                  }}
                />
              </div>
            )}

            {/* Detailed View of All Households - Full Page */}
            {showDetailedView && dashboardTab === "all_households" && (
              <div className="card bg-gradient-to-br from-white via-blue-50/20 to-white border-blue-200/50 shadow-lg min-h-[calc(100vh-200px)] p-5">

                <div className="flex items-center justify-between mb-6 pb-5 border-b border-blue-200/50">
                  <div className="flex items-center gap-4">
                    {/* "All Households" header removed — the tab above already labels this view. */}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleRefreshHouseholds}
                      disabled={householdsLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors text-sm font-semibold text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {householdsLoading ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Refresh
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Filters and Sort Section */}
                <div className="mb-6 pb-4 border-b border-gray-200">
                  <div className="flex flex-wrap items-end gap-3 px-2">
                    {/* Search */}
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Search Households
                      </label>
                      <input
                        type="text"
                        value={householdSearchTerm}
                        onChange={(e) => setHouseholdSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleApplyFilters();
                        }}
                        placeholder="Search by household name..."
                        className="input-modern text-sm w-full"
                      />
                    </div>

                    {/* Filter by Advisor */}
                    <div className="w-[180px]">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Filter by Advisor
                      </label>
                      <select
                        value={selectedAdvisorId || ""}
                        onChange={(e) => setSelectedAdvisorId(e.target.value)}
                        className="input-modern text-sm w-full"
                      >
                        <option value="">All Advisors</option>
                        {project?.advisors?.map((advisor, index) => {
                          const label =
                            `${advisor.first_name || ""} ${advisor.last_name || ""}`.trim() ||
                            advisor.g_number ||
                            `Advisor ${index + 1}`;
                          return (
                            <option
                              key={advisor.user_id || advisor.id || index}
                              value={advisor.user_id || String(index)}
                            >
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Filter by Status */}
                    <div className="w-[180px]">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Filter by Status
                      </label>
                      <select
                        value={selectedStatus || ""}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="input-modern text-sm w-full"
                      >
                        <option value="">All Statuses</option>
                        {STATUS_FILTER_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {labelForStatus(s)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Sort By */}
                    <div className="w-[200px]">
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Sort Order
                      </label>
                      <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                          const val = e.target.value;
                          const lastDash = val.lastIndexOf("-");
                          const newSortBy = val.substring(0, lastDash);
                          const newSortOrder = val.substring(lastDash + 1);
                          setSortBy(newSortBy);
                          setSortOrder(newSortOrder);
                        }}
                        className="input-modern text-sm w-full"
                      >
                        <option value="updated_at-desc">
                          Recently Updated
                        </option>
                        <option value="completion_percentage-desc">
                          Completion % (High → Low)
                        </option>
                        <option value="completion_percentage-asc">
                          Completion % (Low → High)
                        </option>
                        <option value="aum-desc">AUM (High → Low)</option>
                        <option value="aum-asc">AUM (Low → High)</option>
                        <option value="updated_at-asc">Oldest Updated</option>
                        <option value="created_at-desc">
                          Recently Created
                        </option>
                        <option value="created_at-asc">Oldest Created</option>
                        <option value="name-asc">Household Name (A-Z)</option>
                        <option value="name-desc">Household Name (Z-A)</option>
                        <option value="status-asc">Status (A-Z)</option>
                        <option value="status-desc">Status (Z-A)</option>
                      </select>
                    </div>

                    {/* Search Button */}
                    <div className="relative">
                      {hasPendingFilterChanges && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -inset-1 rounded-xl bg-amber-400/40 blur-sm animate-pulse"
                        />
                      )}
                      <button
                        onClick={handleApplyFilters}
                        aria-label={
                          hasPendingFilterChanges
                            ? "Apply filter and sort changes"
                            : "Search households"
                        }
                        title={
                          hasPendingFilterChanges
                            ? "Filters changed — click to apply"
                            : undefined
                        }
                        className={`relative font-semibold py-2 px-6 rounded-lg transition-all duration-300 flex items-center gap-2 h-[42px] text-white ${
                          hasPendingFilterChanges
                            ? "bg-amber-500 hover:bg-amber-600 shadow-md shadow-amber-500/40 ring-2 ring-amber-300"
                            : "bg-blue-600 hover:bg-blue-700"
                        }`}
                      >
                        {hasPendingFilterChanges ? (
                          <ArrowRight className="w-4 h-4" />
                        ) : (
                          <Search className="w-4 h-4" />
                        )}
                        {hasPendingFilterChanges ? "Apply changes" : "Search"}
                      </button>
                    </div>
                  </div>

                  {/* Clear Filters Button */}
                  {(selectedAdvisorId ||
                    selectedStatus ||
                    householdSearchTerm ||
                    sortBy !== "updated_at") && (
                    <div className="flex justify-start px-2 mt-3">
                      <button
                        onClick={handleClearFilters}
                        className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                        Clear all filters
                      </button>
                    </div>
                  )}

                  <SavedFiltersPanel
                    projectId={projectId}
                    project={project}
                    views={savedViews}
                    setViews={setSavedViews}
                    loading={savedViewsLoading}
                    getCurrentFilters={getCurrentFilters}
                    onApplyView={handleApplyView}
                    activeViewId={activeViewId}
                  />
                </div>

                {/* Pagination Controls */}
                {totalItems > 0 && (
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div className="text-sm text-gray-600 font-medium">
                      Showing {(detailedViewPage - 1) * detailedViewPerPage + 1}{" "}
                      to{" "}
                      {Math.min(
                        detailedViewPage * detailedViewPerPage,
                        totalItems,
                      )}{" "}
                      of {totalItems} households
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          setDetailedViewPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={detailedViewPage === 1}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-colors flex items-center gap-1"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Prev
                      </button>
                      <span className="text-sm font-semibold text-gray-700 px-3">
                        Page {detailedViewPage} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setDetailedViewPage((prev) =>
                            Math.min(totalPages, prev + 1),
                          )
                        }
                        disabled={detailedViewPage >= totalPages}
                        className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-colors flex items-center gap-1"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Table Header Row */}
                {!householdsLoading && households.length > 0 && (
                  <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 mb-2 items-center">
                    {/* Name */}
                    <div className="col-span-3">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Household Name
                      </span>
                    </div>

                    {/* Members & Accounts */}
                    <div className="col-span-2">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        # Members / Accounts
                      </span>
                    </div>

                    {project?.aum_availability !== 3 && (
                      <div className="col-span-1">
                        <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                          AUM
                        </span>
                      </div>
                    )}

                    {/* Advisor */}
                    <div className="col-span-1">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Advisor
                      </span>
                    </div>

                    {/* Status */}
                    <div className="col-span-2 flex justify-center items-center">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Status
                      </span>
                    </div>

                    {/* Comments */}
                    <div className="col-span-1 flex justify-center items-center">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Comments
                      </span>
                    </div>

                    {/* Completion % */}
                    <div className="col-span-1">
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Progress
                      </span>
                    </div>

                    {/* Created On */}
                    <div className={project?.aum_availability !== 3 ? "col-span-1" : "col-span-2"}>
                      <span className="text-xs font-bold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        Created On
                      </span>
                    </div>
                  </div>
                )}

                {/* Scrollable Household List - Compact Row Format */}
                <div className="max-h-[calc(100vh-350px)] overflow-y-auto pr-2">
                  <div className="space-y-2">
                    {householdsLoading ? (
                      <div className="text-center py-12">
                        <div className="flex flex-col items-center justify-center">
                          <Loader2 className="animate-spin h-10 w-10 text-blue-500 mb-4" />
                          <p className="text-gray-600 font-medium text-sm">
                            Loading households...
                          </p>
                        </div>
                      </div>
                    ) : households.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          No households found
                        </h3>
                      </div>
                    ) : (
                      <>
                        {households.map((household) => {
                          return (
                            <div
                              key={household.id}
                              className="p-4 rounded-xl border transition-all duration-200 group cursor-pointer hover:shadow-md border-gray-200 hover:border-blue-400 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-emerald-50/30"
                              onClick={() => handleHouseholdClick(household.id)}
                            >
                              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center w-full">
                                {/* Household Name */}
                                <div className="col-span-1 md:col-span-3 min-w-0">
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    Name
                                  </div>
                                  <div className="flex items-center gap-2 min-w-0">
                                    <NeedsAttentionMark active={!!household.in_needs_attention} />
                                    <h3 className="text-sm font-bold text-gray-900 group-hover:text-blue-700 transition-colors truncate">
                                      {household.name}
                                    </h3>
                                  </div>
                                </div>

                                {/* Counts */}
                                <div className="col-span-1 md:col-span-2 flex items-center gap-2">
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    Counts
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-md"
                                      title="Members"
                                    >
                                      <Users className="w-3.5 h-3.5 text-blue-600" />
                                      <span className="text-xs font-semibold text-blue-700">
                                        {household.member_count || 0}
                                      </span>
                                    </div>
                                    <div
                                      className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded-md"
                                      title="Accounts"
                                    >
                                      <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                                      <span className="text-xs font-semibold text-emerald-700">
                                        {household.account_count || 0}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {project?.aum_availability !== 3 && (
                                  <div className="col-span-1">
                                    <div className="text-xs text-gray-500 mb-1 md:hidden">
                                      AUM
                                    </div>
                                    <span className="text-xs font-semibold text-gray-900 whitespace-nowrap">
                                      {household.aum != null
                                        ? `$${formatCompactCurrency(household.aum)}`
                                        : "—"}
                                    </span>
                                  </div>
                                )}

                                {/* Advisor Name */}
                                <div className="col-span-1">
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    Advisor
                                  </div>
                                  <div className="flex items-center gap-1.5 overflow-hidden">
                                    <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="text-xs font-medium text-gray-700 truncate">
                                      {household.advisor_first_name
                                        ? `${household.advisor_first_name} ${household.advisor_last_name || ""}`
                                        : "Unassigned"}
                                    </span>
                                  </div>
                                </div>

                                {/* Status */}
                                <div className="col-span-2 flex justify-center">
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    Status
                                  </div>
                                  <StatusBadge
                                    status={household.status}
                                    subStatus={household.sub_status}
                                    staleHousehold={!!household.stale_household}
                                  />
                                </div>

                                {/* Comments */}
                                <div className="col-span-1 flex justify-center">
                                  <CommentMark
                                    commentState={household.comment_state}
                                    unreadCount={household.unread_comment_count}
                                  />
                                </div>

                                {/* % Completion */}
                                <div className="col-span-1">
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    % Completion
                                  </div>
                                  {household.completion_percentage !==
                                    undefined && (
                                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 rounded-full border border-amber-100/50">
                                      <div className="flex-shrink-0 w-3.5 h-3.5 rounded-full border border-amber-500 flex items-center justify-center bg-white">
                                        <Check className="w-2.5 h-2.5 text-amber-600" strokeWidth={3} />
                                      </div>
                                      <span className="text-xs font-bold text-amber-900">
                                        {Math.floor(
                                          household.completion_percentage,
                                        )}
                                        %
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Data Created */}
                                <div className={project?.aum_availability !== 3 ? "col-span-1" : "col-span-2"}>
                                  <div className="text-xs text-gray-500 mb-1 md:hidden">
                                    Created
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-medium text-gray-900">
                                      {formatDateForDisplay(
                                        household.created_at ||
                                          household.excel_upload_date ||
                                          new Date().toISOString(),
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Household Detail Modal */}
      {selectedHousehold && (
        <div
          className="modal-overlay"
          onClick={() => setSelectedHousehold(null)}
        >
          <div
            className="modal-content max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-emerald-50/10 to-blue-50/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6 pb-5 border-b border-emerald-200/50">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gray-900 rounded-2xl flex items-center justify-center shadow-lg">
                  <Home className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900">
                  {selectedHousehold.name}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDownloadHouseholdForms}
                  disabled={downloadingHouseholdForms}
                  className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap"
                >
                  {downloadingHouseholdForms ? (
                    <>
                      <Loader2 className="animate-spin h-3.5 w-3.5" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-3.5 h-3.5" />
                      Populate Forms
                    </>
                  )}
                </button>
                <div
                  className="relative inline-block"
                  title={
                    !hasPrimaryMemberWithEmail()
                      ? "No email exists for the client"
                      : "Request Client Info"
                  }
                >
                  <button
                    onClick={handleSendToClient}
                    disabled={sendingToClient || !hasPrimaryMemberWithEmail()}
                    className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                  >
                    {sendingToClient ? (
                      <>
                        <Loader2 className="animate-spin w-3.5 h-3.5" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-3.5 h-3.5" />
                        {[
                          "shared_with_client",
                          "shared_with_client_onboarding_done",
                          "shared_with_client_for_final_review",
                        ].includes(selectedHousehold.status)
                          ? "Remind Client"
                          : "Request Client Info"}
                      </>
                    )}
                  </button>
                </div>
                <button
                  className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap"
                  onClick={() => setShowUploadDataModal(true)}
                  disabled={!selectedHousehold}
                >
                  <UploadCloud className="w-3.5 h-3.5" />
                  Enrich Household Record
                </button>
                <button
                  className="btn-primary text-xs px-3 py-2 flex items-center gap-1.5 whitespace-nowrap"
                  title="Edit"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Edit
                </button>
                <button
                  onClick={() => setSelectedHousehold(null)}
                  className="btn-ghost p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Completion Percentage */}
            {selectedHousehold.completion_percentage !== undefined && (
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                    <div>
                      <div className="text-sm font-medium text-emerald-700">
                        Data Completion
                      </div>
                      <div className="text-2xl font-bold text-emerald-900">
                        {Math.floor(selectedHousehold.completion_percentage)}%
                      </div>
                    </div>
                  </div>
                  <div className="w-32 bg-emerald-200 rounded-full h-3">
                    <div
                      className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.floor(selectedHousehold.completion_percentage)}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-3 mb-6 pb-2 border-b-2 border-gray-200">
              <button
                onClick={() => setActiveTab("household")}
                className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
                  activeTab === "household"
                    ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Household Info
              </button>
              <button
                onClick={() => setActiveTab("accounts")}
                className={`px-6 py-3 font-semibold rounded-t-xl transition-all duration-200 ${
                  activeTab === "accounts"
                    ? "text-emerald-700 bg-gradient-to-b from-emerald-50 to-transparent border-b-2 border-emerald-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Accounts ({selectedHousehold.accounts?.length || 0})
              </button>
            </div>

            {householdLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
                <p className="mt-4 text-gray-600 font-medium">
                  Loading household details
                </p>
              </div>
            ) : (
              <>
                {/* Household Info Tab */}
                {activeTab === "household" && (
                  <div className="space-y-6">
                    {/* Missing Fields Section */}
                    {missingFields && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-300">
                        <div className="flex items-start gap-3">
                          <TriangleAlert className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900">
                                Edit Pending Data
                              </h4>
                              <button
                                onClick={() =>
                                  setMissingFieldsExpanded(
                                    !missingFieldsExpanded,
                                  )
                                }
                                className="text-amber-700 hover:text-amber-900 transition-colors"
                                aria-label={
                                  missingFieldsExpanded ? "Collapse" : "Expand"
                                }
                              >
                                <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${missingFieldsExpanded ? "rotate-180" : ""}`} />
                              </button>
                            </div>

                            {missingFieldsExpanded && (
                              <div>
                                <p className="text-sm text-gray-700 mb-3">
                                  Click any button below to complete details
                                </p>

                                {/* Household Missing Fields */}
                                {missingFields.household &&
                                  missingFields.household.length > 0 && (
                                    <div className="mb-3">
                                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                                        Household:
                                      </h5>
                                      <div className="flex flex-wrap gap-2">
                                        {missingFields.household.map(
                                          (field, idx) => (
                                            <button
                                              key={idx}
                                              onClick={() =>
                                                navigateToField(
                                                  "household",
                                                  field,
                                                )
                                              }
                                              className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                            >
                                              {field
                                                .replace(/_/g, " ")
                                                .replace(/\b\w/g, (l) =>
                                                  l.toUpperCase(),
                                                )}
                                            </button>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}

                                {/* Household Members Missing Fields */}
                                {missingFields.household_members &&
                                  Object.keys(missingFields.household_members)
                                    .length > 0 && (
                                    <div className="mb-3">
                                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                                        Household Members:
                                      </h5>
                                      {Object.entries(
                                        missingFields.household_members,
                                      ).map(([memberId, fields]) => {
                                        // Find member by ID to get first name
                                        const member =
                                          selectedHousehold?.members?.find(
                                            (m) => m.id === memberId,
                                          );
                                        const memberFirstName =
                                          member?.personal_info?.first_name ||
                                          "Unknown Member";
                                        return (
                                          <div key={memberId} className="mb-2">
                                            <span className="text-xs text-gray-600">
                                              {memberFirstName}:
                                            </span>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                              {fields.map((field, idx) => (
                                                <button
                                                  key={idx}
                                                  onClick={() =>
                                                    navigateToField(
                                                      "household_members",
                                                      field,
                                                      memberId,
                                                    )
                                                  }
                                                  className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                                >
                                                  {field
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (l) =>
                                                      l.toUpperCase(),
                                                    )}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                {/* Accounts Missing Fields */}
                                {missingFields.accounts &&
                                  Object.keys(missingFields.accounts).length >
                                    0 && (
                                    <div>
                                      <h5 className="text-sm font-semibold text-gray-800 mb-2">
                                        Accounts:
                                      </h5>
                                      {Object.entries(
                                        missingFields.accounts,
                                      ).map(([accountId, fields]) => {
                                        // Find account by ID to get old_account_number
                                        const account =
                                          selectedHousehold?.accounts?.find(
                                            (a) => a.id === accountId,
                                          );
                                        const accountNumber =
                                          account?.transfer_info
                                            ?.old_account_number ||
                                          account?.new_account_number ||
                                          "Unknown Account";
                                        return (
                                          <div key={accountId} className="mb-2">
                                            <span className="text-xs text-gray-600">
                                              Account {accountNumber}:
                                            </span>
                                            <div className="flex flex-wrap gap-2 mt-1">
                                              {fields.map((field, idx) => (
                                                <button
                                                  key={idx}
                                                  onClick={() =>
                                                    navigateToField(
                                                      "accounts",
                                                      field,
                                                      null,
                                                      accountId,
                                                    )
                                                  }
                                                  className="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 text-xs font-medium rounded-lg transition duration-200 border border-amber-300"
                                                >
                                                  {field
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (l) =>
                                                      l.toUpperCase(),
                                                    )}
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Household Basic Info */}
                    <div
                      className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100"
                      data-section="household-info"
                    >
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-bold text-gray-900">
                          Household Information
                        </h3>
                        {!editingHousehold ? (
                          <button
                            onClick={() => setEditingHousehold(true)}
                            className="btn-primary flex items-center gap-2"
                          >
                            <Pencil className="w-4 h-4" />
                            Edit
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingHousehold(false);
                                // Reset form data
                                fetchHouseholdDetails(selectedHousehold.id);
                              }}
                              className="btn-ghost"
                              disabled={savingHousehold}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveHousehold}
                              className="btn-primary flex items-center gap-2"
                              disabled={savingHousehold}
                            >
                              {savingHousehold ? (
                                <>
                                  <Loader2 className="animate-spin h-4 w-4" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4" />
                                  Save
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {showIncomeDetails(brokerageFlags, selectedHousehold?.accounts) && (
                        <>
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Annual Income
                          </div>
                          {editingHousehold ? (
                            <input
                              type="number"
                              value={householdFormData.annual_income || ""}
                              onChange={(e) =>
                                handleAnnualIncomeChange(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Enter annual income"
                            />
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.annual_income
                                ? `$${selectedHousehold.annual_income.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Total Net Worth
                          </div>
                          {editingHousehold ? (
                            <input
                              type="number"
                              value={
                                householdFormData.estimated_total_net_worth ||
                                ""
                              }
                              onChange={(e) =>
                                handleTotalNetWorthChange(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Enter total net worth"
                            />
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.estimated_total_net_worth
                                ? `$${selectedHousehold.estimated_total_net_worth.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Liquid Net Worth
                          </div>
                          {editingHousehold ? (
                            <input
                              type="number"
                              value={
                                householdFormData.estimated_liquid_net_worth ||
                                ""
                              }
                              onChange={(e) =>
                                handleLiquidNetWorthChange(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Enter liquid net worth"
                            />
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.estimated_liquid_net_worth
                                ? `$${selectedHousehold.estimated_liquid_net_worth.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        </>
                        )}
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Tax Percentage
                          </div>
                          {editingHousehold ? (
                            <input
                              type="number"
                              step="0.01"
                              value={householdFormData.tax_percentage || ""}
                              onChange={(e) =>
                                handleTaxPercentageChange(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                              placeholder="Enter tax percentage"
                            />
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.tax_percentage
                                ? `${selectedHousehold.tax_percentage}%`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        {showAnnualExpenseRange(brokerageFlags) && (
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Annual Expense Range
                          </div>
                          {editingHousehold ? (
                            <div className="space-y-2">
                              <div className="flex gap-2 items-center">
                                <label className="text-xs text-gray-600 w-12">
                                  From:
                                </label>
                                <input
                                  type="number"
                                  value={
                                    householdFormData.annual_expense_range
                                      ?.start || ""
                                  }
                                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val !== "" && parseFloat(val) < 0)
                                      val = "0";
                                    handleNestedFieldChange(
                                      "annual_expense_range",
                                      "start",
                                      val,
                                    );
                                  }}
                                  onBlur={() =>
                                    handleRangeBlur(
                                      "annual_expense_range",
                                      "start",
                                      "Annual Expenses",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleRangeBlur(
                                        "annual_expense_range",
                                        "start",
                                        "Annual Expenses",
                                      );
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                              </div>
                              <div className="flex gap-2 items-center">
                                <label className="text-xs text-gray-600 w-12">
                                  To:
                                </label>
                                <input
                                  type="number"
                                  value={
                                    householdFormData.annual_expense_range
                                      ?.end || ""
                                  }
                                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val !== "" && parseFloat(val) < 0)
                                      val = "0";
                                    handleNestedFieldChange(
                                      "annual_expense_range",
                                      "end",
                                      val,
                                    );
                                  }}
                                  onBlur={() =>
                                    handleRangeBlur(
                                      "annual_expense_range",
                                      "end",
                                      "Annual Expenses",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleRangeBlur(
                                        "annual_expense_range",
                                        "end",
                                        "Annual Expenses",
                                      );
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.annual_expense_range
                                ? `$${selectedHousehold.annual_expense_range.start?.toLocaleString() || "N/A"} - $${selectedHousehold.annual_expense_range.end?.toLocaleString() || "N/A"}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        )}
                        {showSpecialExpenseRange(brokerageFlags) && (
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Special Expense Range
                          </div>
                          {editingHousehold ? (
                            <div className="space-y-2">
                              <div className="flex gap-2 items-center">
                                <label className="text-xs text-gray-600 w-12">
                                  From:
                                </label>
                                <input
                                  type="number"
                                  value={
                                    householdFormData.special_expense_range
                                      ?.start || ""
                                  }
                                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val !== "" && parseFloat(val) < 0)
                                      val = "0";
                                    handleNestedFieldChange(
                                      "special_expense_range",
                                      "start",
                                      val,
                                    );
                                  }}
                                  onBlur={() =>
                                    handleRangeBlur(
                                      "special_expense_range",
                                      "start",
                                      "Special Expenses",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleRangeBlur(
                                        "special_expense_range",
                                        "start",
                                        "Special Expenses",
                                      );
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                              </div>
                              <div className="flex gap-2 items-center">
                                <label className="text-xs text-gray-600 w-12">
                                  To:
                                </label>
                                <input
                                  type="number"
                                  value={
                                    householdFormData.special_expense_range
                                      ?.end || ""
                                  }
                                  onChange={(e) => {
                                    let val = e.target.value;
                                    if (val !== "" && parseFloat(val) < 0)
                                      val = "0";
                                    handleNestedFieldChange(
                                      "special_expense_range",
                                      "end",
                                      val,
                                    );
                                  }}
                                  onBlur={() =>
                                    handleRangeBlur(
                                      "special_expense_range",
                                      "end",
                                      "Special Expenses",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter")
                                      handleRangeBlur(
                                        "special_expense_range",
                                        "end",
                                        "Special Expenses",
                                      );
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedHousehold.special_expense_range
                                ? `$${selectedHousehold.special_expense_range.start?.toLocaleString() || "N/A"} - $${selectedHousehold.special_expense_range.end?.toLocaleString() || "N/A"}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        )}
                        <div>
                          <div className="text-sm text-gray-500 font-medium mb-1">
                            Status
                          </div>
                          {editingHousehold ? (
                            <select
                              value={householdFormData.status || "active"}
                              onChange={(e) =>
                                handleHouseholdFieldChange(
                                  "status",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          ) : (
                            <div className="text-lg font-semibold text-gray-900">
                              <StatusBadge
                                status={selectedHousehold.status}
                                subStatus={selectedHousehold.sub_status}
                                staleHousehold={
                                  !!selectedHousehold.stale_household
                                }
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Tax Info */}
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          Tax Information
                        </h4>
                        {editingHousehold ? (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">
                              Tax Bracket
                            </div>
                            <select
                              value={(() => {
                                const bracket =
                                  householdFormData.tax_info?.tax_bracket;
                                if (!bracket || bracket.start == null) return "";
                                const entry = Object.entries(
                                  TAX_BRACKET_MAP,
                                ).find(
                                  ([, v]) =>
                                    v.start === bracket.start &&
                                    v.end === bracket.end,
                                );
                                return entry ? entry[0] : "";
                              })()}
                              onChange={(e) => {
                                const range = TAX_BRACKET_MAP[e.target.value];
                                if (range) {
                                  handleDeepNestedFieldChange(
                                    "tax_info",
                                    "tax_bracket",
                                    "start",
                                    range.start,
                                  );
                                  handleDeepNestedFieldChange(
                                    "tax_info",
                                    "tax_bracket",
                                    "end",
                                    range.end,
                                  );
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                              <option value="">Select...</option>
                              {TAX_BRACKET_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div>
                            <div className="text-xs text-gray-500 mb-1">
                              Tax Bracket
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              {(() => {
                                const bracket =
                                  selectedHousehold.tax_info?.tax_bracket;
                                if (!bracket || bracket.start == null)
                                  return "N/A";
                                const entry = Object.entries(
                                  TAX_BRACKET_MAP,
                                ).find(
                                  ([, v]) =>
                                    v.start === bracket.start &&
                                    v.end === bracket.end,
                                );
                                return entry ? entry[0] : "N/A";
                              })()}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Investor Associations */}
                      <div
                        className="mt-6 pt-6 border-t border-gray-200"
                        data-section="investor_associations"
                      >
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          Investor Associations
                        </h4>
                        {editingHousehold ? (
                          <div className="space-y-2">
                            {[
                              "accredited_investor",
                              "qualified_purchaser",
                              "us_broker_dealer_association",
                              "broker_dealer_employee",
                              "ria_association",
                              "shareholder_or_member_board_director",
                              "low_priced_securities",
                            ].map((key) => (
                              <label
                                key={key}
                                className="flex items-center gap-2 cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={
                                    householdFormData.investor_associations?.[
                                      key
                                    ] || false
                                  }
                                  onChange={(e) => {
                                    setHouseholdFormData((prev) => ({
                                      ...prev,
                                      investor_associations: {
                                        ...(prev.investor_associations || {}),
                                        [key]: e.target.checked,
                                      },
                                    }));
                                  }}
                                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <span className="text-sm text-gray-700 capitalize">
                                  {key
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {selectedHousehold.investor_associations
                              ?.accredited_investor && (
                              <span className="badge badge-success">
                                Accredited Investor
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.us_broker_dealer_association && (
                              <span className="badge badge-info">
                                US Broker Dealer
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.broker_dealer_employee && (
                              <span className="badge badge-info">
                                Broker Dealer Employee
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.ria_association && (
                              <span className="badge badge-info">
                                RIA Association
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.shareholder_or_member_board_director && (
                              <span className="badge badge-info">
                                Board Director
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.qualified_purchaser && (
                              <span className="badge badge-success">
                                Qualified Purchaser
                              </span>
                            )}
                            {selectedHousehold.investor_associations
                              ?.low_priced_securities && (
                              <span className="badge badge-info">
                                Low-Priced Securities
                              </span>
                            )}
                            {!selectedHousehold.investor_associations && (
                              <span className="text-sm text-gray-500">
                                No associations set
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Investor Experience */}
                      <div
                        className="mt-6 pt-6 border-t border-gray-200"
                        data-section="investor_experience"
                      >
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">
                          Investor Experience (Years)
                        </h4>
                        {editingHousehold ? (
                          <div className="space-y-3">
                            {[
                              "bonds",
                              "stocks",
                              "alternative_investments",
                              "variable_annuities",
                              "mutual_funds",
                              "options",
                              "partnerships",
                            ]
                              .filter((key) =>
                                showExperienceField(
                                  key,
                                  brokerageFlags,
                                  selectedHousehold?.accounts,
                                ),
                              )
                              .map((key) => (
                              <div
                                key={key}
                                className="flex items-center gap-3"
                              >
                                <label className="text-sm text-gray-700 capitalize w-48">
                                  {key
                                    .replace(/_/g, " ")
                                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  :
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={
                                    householdFormData.investor_experience?.[
                                      key
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    handleExperienceChange(key, e.target.value)
                                  }
                                  onBlur={(e) => {
                                    if (
                                      e.target.value === "" ||
                                      e.target.value === null
                                    ) {
                                      handleExperienceChange(key, "");
                                    }
                                  }}
                                  className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-emerald-500 focus:border-emerald-500"
                                  placeholder="0"
                                />
                                <span className="text-xs text-gray-500">
                                  years
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {[
                              "bonds",
                              "stocks",
                              "alternative_investments",
                              "variable_annuities",
                              "mutual_funds",
                              "options",
                              "partnerships",
                            ]
                              .filter((key) =>
                                showExperienceField(
                                  key,
                                  brokerageFlags,
                                  selectedHousehold?.accounts,
                                ),
                              )
                              .map((key) => {
                              const value =
                                selectedHousehold.investor_experience?.[key];
                              if (
                                value === null ||
                                value === undefined ||
                                value === ""
                              )
                                return null;
                              return (
                                <div
                                  key={key}
                                  className="flex items-center gap-3"
                                >
                                  <span className="text-sm text-gray-700 capitalize w-48">
                                    {key
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                    :
                                  </span>
                                  <span className="text-sm font-semibold text-gray-900">
                                    {value} {value === 1 ? "year" : "years"}
                                  </span>
                                </div>
                              );
                            })}
                            {(!selectedHousehold.investor_experience ||
                              Object.values(
                                selectedHousehold.investor_experience,
                              ).every(
                                (v) =>
                                  v === null || v === undefined || v === "",
                              )) && (
                              <span className="text-sm text-gray-500">
                                No experience set
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Members Section */}
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        Household Members (
                        {selectedHousehold.members?.length || 0})
                      </h3>
                      {selectedHousehold.members &&
                      selectedHousehold.members.length > 0 ? (
                        <div className="space-y-4">
                          {selectedHousehold.members.map((member) => {
                            const personalInfo = member.personal_info || {};
                            const fullName =
                              [
                                personalInfo.first_name,
                                personalInfo.middle_name,
                                personalInfo.last_name,
                              ]
                                .filter(Boolean)
                                .join(" ") || "N/A";

                            const isEditing = editingMemberId === member.id;

                            return (
                              <div
                                key={member.id}
                                data-member-id={member.id}
                                className="bg-white rounded-xl p-5 border border-gray-200 hover:border-emerald-300 transition-all"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <h4 className="text-lg font-bold text-gray-900">
                                        {fullName}
                                      </h4>
                                      {member.is_primary && (
                                        <span className="badge badge-success">
                                          Primary
                                        </span>
                                      )}
                                      {isEditing ? (
                                        <select
                                          value={memberFormData.type || "human"}
                                          onChange={(e) =>
                                            setMemberFormData((prev) => ({
                                              ...prev,
                                              type: e.target.value,
                                            }))
                                          }
                                          className="badge badge-info border-0"
                                        >
                                          <option value="human">Human</option>
                                          <option value="trust">Trust</option>
                                        </select>
                                      ) : (
                                        <span className="badge badge-info">
                                          {member.type}
                                        </span>
                                      )}
                                    </div>
                                    {!isEditing ? (
                                      <button
                                        onClick={() => handleEditMember(member)}
                                        className="btn-primary text-sm px-3 py-1 mb-4"
                                      >
                                        Edit
                                      </button>
                                    ) : (
                                      <div className="flex gap-2 mb-4">
                                        <button
                                          onClick={() => {
                                            setEditingMemberId(null);
                                            setMemberFormData({});
                                          }}
                                          className="btn-ghost text-sm px-3 py-1"
                                          disabled={savingMember}
                                        >
                                          Cancel
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleSaveMember(member.id)
                                          }
                                          className="btn-primary text-sm px-3 py-1"
                                          disabled={savingMember}
                                        >
                                          {savingMember ? "Saving..." : "Save"}
                                        </button>
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          First Name
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.first_name || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  first_name: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.first_name || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Middle Name
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.middle_name || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  middle_name: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.middle_name || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Last Name
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.last_name || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  last_name: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.last_name || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Email
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="email"
                                            value={
                                              memberFormData.personal_info
                                                ?.email || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  email: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.email || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Mobile
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="tel"
                                            value={
                                              memberFormData.personal_info
                                                ?.mobile_number || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  mobile_number: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.mobile_number ||
                                              "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Date of Birth
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="date"
                                            max={getTodayYmdLocal()}
                                            value={
                                              memberFormData.personal_info
                                                ?.dob || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  dob: e.target.value,
                                                },
                                              }))
                                            }
                                            onKeyDown={(e) =>
                                              e.preventDefault()
                                            }
                                            onPaste={(e) => e.preventDefault()}
                                            onWheel={(e) => e.target.blur()}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {formatDate(personalInfo.dob)}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Title
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.title || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  title: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.title || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      {showMaritalStatus(brokerageFlags) && (
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Marital Status
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.marital_status || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  marital_status:
                                                    e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.marital_status ||
                                              "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      )}
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          SSN/TIN
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.ssn_or_tin || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  ssn_or_tin: e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.ssn_or_tin || "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Preferred Name
                                        </div>
                                        {isEditing ? (
                                          <input
                                            type="text"
                                            value={
                                              memberFormData.personal_info
                                                ?.preferred_name || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  preferred_name:
                                                    e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          />
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.preferred_name ||
                                              "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Country of Citizenship
                                        </div>
                                        {isEditing ? (
                                          <select
                                            value={
                                              memberFormData.personal_info
                                                ?.country_of_citizenship || ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  country_of_citizenship:
                                                    e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          >
                                            <option value="">Select</option>
                                            {COUNTRY_OPTIONS.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.country_of_citizenship ||
                                              "N/A"}
                                          </div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-sm text-gray-500 font-medium mb-1">
                                          Country of Legal Residence
                                        </div>
                                        {isEditing ? (
                                          <select
                                            value={
                                              memberFormData.personal_info
                                                ?.country_of_legal_residence ||
                                              ""
                                            }
                                            onChange={(e) =>
                                              setMemberFormData((prev) => ({
                                                ...prev,
                                                personal_info: {
                                                  ...(prev.personal_info || {}),
                                                  country_of_legal_residence:
                                                    e.target.value,
                                                },
                                              }))
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                          >
                                            <option value="">Select</option>
                                            {COUNTRY_OPTIONS.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        ) : (
                                          <div className="text-sm text-gray-900">
                                            {personalInfo.country_of_legal_residence ||
                                              "N/A"}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Addresses */}
                                    {((personalInfo.address &&
                                      Array.isArray(personalInfo.address) &&
                                      personalInfo.address.length > 0) ||
                                      isEditing) && (
                                      <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="text-sm font-semibold text-gray-700 mb-2">
                                          Addresses
                                        </div>
                                        {isEditing ? (
                                          <div className="space-y-4">
                                            {(memberFormData.personal_info
                                              ?.address &&
                                            Array.isArray(
                                              memberFormData.personal_info
                                                .address,
                                            )
                                              ? memberFormData.personal_info
                                                  .address
                                              : []
                                            ).map((addr, idx) => (
                                              <div
                                                key={idx}
                                                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                                              >
                                                <div className="flex items-center justify-between mb-3">
                                                  <div className="text-xs font-semibold text-gray-600 uppercase">
                                                    {addr.type || "Address"}{" "}
                                                    {idx + 1}
                                                  </div>
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      const newAddresses = [
                                                        ...(memberFormData
                                                          .personal_info
                                                          ?.address || []),
                                                      ];
                                                      newAddresses.splice(
                                                        idx,
                                                        1,
                                                      );
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          personal_info: {
                                                            ...(prev.personal_info ||
                                                              {}),
                                                            address:
                                                              newAddresses,
                                                          },
                                                        }),
                                                      );
                                                    }}
                                                    className="text-xs text-red-600 hover:text-red-800"
                                                  >
                                                    Remove
                                                  </button>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Type
                                                    </label>
                                                    <select
                                                      value={
                                                        addr.type || "home"
                                                      }
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          type: e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    >
                                                      <option value="home">
                                                        Home
                                                      </option>
                                                      <option value="mailing">
                                                        Mailing
                                                      </option>
                                                      <option value="work">
                                                        Work
                                                      </option>
                                                    </select>
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Address Line 1
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={
                                                        addr.address_line_1 ||
                                                        ""
                                                      }
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          address_line_1:
                                                            e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Address Line 2
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={
                                                        addr.address_line_2 ||
                                                        ""
                                                      }
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          address_line_2:
                                                            e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      City
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={addr.city || ""}
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          city: e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Province/State
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={
                                                        addr.province || ""
                                                      }
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          province:
                                                            e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Postal Code
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={
                                                        addr.postal_code || ""
                                                      }
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          postal_code:
                                                            e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Country
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={addr.country || ""}
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          country:
                                                            e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                  <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">
                                                      Phone
                                                    </label>
                                                    <input
                                                      type="text"
                                                      value={addr.phone || ""}
                                                      onChange={(e) => {
                                                        const newAddresses = [
                                                          ...(memberFormData
                                                            .personal_info
                                                            ?.address || []),
                                                        ];
                                                        newAddresses[idx] = {
                                                          ...newAddresses[idx],
                                                          phone: e.target.value,
                                                        };
                                                        setMemberFormData(
                                                          (prev) => ({
                                                            ...prev,
                                                            personal_info: {
                                                              ...(prev.personal_info ||
                                                                {}),
                                                              address:
                                                                newAddresses,
                                                            },
                                                          }),
                                                        );
                                                      }}
                                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                    />
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const newAddresses = [
                                                  ...(memberFormData
                                                    .personal_info?.address ||
                                                    []),
                                                  {
                                                    type: "home",
                                                    address_line_1: "",
                                                    address_line_2: "",
                                                    city: "",
                                                    province: "",
                                                    postal_code: "",
                                                    country: "USA",
                                                    phone: "",
                                                  },
                                                ];
                                                setMemberFormData((prev) => ({
                                                  ...prev,
                                                  personal_info: {
                                                    ...(prev.personal_info ||
                                                      {}),
                                                    address: newAddresses,
                                                  },
                                                }));
                                              }}
                                              className="text-sm text-emerald-600 hover:text-emerald-800 font-medium"
                                            >
                                              + Add Address
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="space-y-3">
                                            {personalInfo.address.map(
                                              (addr, idx) => (
                                                <div
                                                  key={idx}
                                                  className="bg-gray-50 rounded-lg p-3"
                                                >
                                                  <div className="text-xs font-semibold text-gray-600 mb-1 uppercase">
                                                    {addr.type || "Address"}
                                                  </div>
                                                  <div className="text-sm text-gray-900">
                                                    {addr.address_line_1 && (
                                                      <div>
                                                        {addr.address_line_1}
                                                      </div>
                                                    )}
                                                    {addr.address_line_2 && (
                                                      <div>
                                                        {addr.address_line_2}
                                                      </div>
                                                    )}
                                                    <div>
                                                      {[
                                                        addr.city,
                                                        addr.province,
                                                        addr.postal_code,
                                                      ]
                                                        .filter(Boolean)
                                                        .join(", ")}
                                                    </div>
                                                    {addr.country && (
                                                      <div>{addr.country}</div>
                                                    )}
                                                    {addr.phone && (
                                                      <div className="mt-1 text-gray-600">
                                                        {addr.phone}
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* ID Info */}
                                    {showIdInfo(brokerageFlags) &&
                                      member.id_info &&
                                      Array.isArray(member.id_info) &&
                                      member.id_info.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                          <div className="text-sm font-semibold text-gray-700 mb-2">
                                            ID Information
                                          </div>
                                          <div className="space-y-3">
                                            {member.id_info.map((id, idx) => (
                                              <div
                                                key={idx}
                                                className="bg-gray-50 rounded-lg p-3"
                                              >
                                                <div className="grid grid-cols-2 gap-3">
                                                  <div>
                                                    <div className="text-xs text-gray-500 mb-1">
                                                      Type
                                                    </div>
                                                    <div className="text-sm text-gray-900">
                                                      {id.type}
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <div className="text-xs text-gray-500 mb-1">
                                                      Number
                                                    </div>
                                                    <div className="text-sm text-gray-900">
                                                      {id.number}
                                                    </div>
                                                  </div>
                                                  {id.issue_state && (
                                                    <div>
                                                      <div className="text-xs text-gray-500 mb-1">
                                                        Issue State
                                                      </div>
                                                      <div className="text-sm text-gray-900">
                                                        {id.issue_state}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {id.issuance_date && (
                                                    <div>
                                                      <div className="text-xs text-gray-500 mb-1">
                                                        Issuance Date
                                                      </div>
                                                      <div className="text-sm text-gray-900">
                                                        {formatDate(
                                                          id.issuance_date,
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                  {id.expiration_date && (
                                                    <div>
                                                      <div className="text-xs text-gray-500 mb-1">
                                                        Expiration Date
                                                      </div>
                                                      <div className="text-sm text-gray-900">
                                                        {formatDate(
                                                          id.expiration_date,
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                    {/* Employment Info */}
                                    {member.employment_info && (
                                      <div className="mt-4 pt-4 border-t border-gray-100">
                                        <div className="text-sm font-semibold text-gray-700 mb-2">
                                          Employment
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                          {member.employment_info
                                            .occupation && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Occupation
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {
                                                  member.employment_info
                                                    .occupation
                                                }
                                              </div>
                                            </div>
                                          )}
                                          {member.employment_info.employer && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Employer
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {
                                                  member.employment_info
                                                    .employer
                                                }
                                              </div>
                                            </div>
                                          )}
                                          {/* industry removed from UI */}
                                        </div>
                                        {(member.employment_info?.address ||
                                          (isEditing &&
                                            memberFormData.employment_info
                                              ?.address)) && (
                                          <div className="mt-3 bg-gray-50 rounded-lg p-3">
                                            <div className="text-xs font-semibold text-gray-600 mb-2">
                                              Work Address
                                            </div>
                                            {isEditing ? (
                                              <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Address Line 1
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address
                                                        ?.address_line_1 || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              address_line_1:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Address Line 2
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address
                                                        ?.address_line_2 || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              address_line_2:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    City
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address?.city || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              city: e.target
                                                                .value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Province/State
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address?.province ||
                                                      ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              province:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Postal Code
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address
                                                        ?.postal_code || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              postal_code:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Country
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address?.country || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              country:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                                <div>
                                                  <label className="text-xs text-gray-500 mb-1 block">
                                                    Phone
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={
                                                      memberFormData
                                                        .employment_info
                                                        ?.address?.phone || ""
                                                    }
                                                    onChange={(e) =>
                                                      setMemberFormData(
                                                        (prev) => ({
                                                          ...prev,
                                                          employment_info: {
                                                            ...(prev.employment_info ||
                                                              {}),
                                                            address: {
                                                              ...(prev
                                                                .employment_info
                                                                ?.address ||
                                                                {}),
                                                              phone:
                                                                e.target.value,
                                                            },
                                                          },
                                                        }),
                                                      )
                                                    }
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                                  />
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="text-sm text-gray-900">
                                                {member.employment_info.address
                                                  .address_line_1 && (
                                                  <div>
                                                    {
                                                      member.employment_info
                                                        .address.address_line_1
                                                    }
                                                  </div>
                                                )}
                                                {member.employment_info.address
                                                  .address_line_2 && (
                                                  <div>
                                                    {
                                                      member.employment_info
                                                        .address.address_line_2
                                                    }
                                                  </div>
                                                )}
                                                <div>
                                                  {[
                                                    member.employment_info
                                                      .address.city,
                                                    member.employment_info
                                                      .address.province,
                                                    member.employment_info
                                                      .address.postal_code,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(", ")}
                                                </div>
                                                {member.employment_info.address
                                                  .country && (
                                                  <div>
                                                    {
                                                      member.employment_info
                                                        .address.country
                                                    }
                                                  </div>
                                                )}
                                                {member.employment_info.address
                                                  .phone && (
                                                  <div className="mt-1 text-gray-600">
                                                    {
                                                      member.employment_info
                                                        .address.phone
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-xl p-6 text-center">
                          <p className="text-gray-500 font-medium">
                            No members found
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Accounts Tab */}
                {activeTab === "accounts" && (
                  <div className="space-y-4">
                    {selectedHousehold.accounts &&
                    selectedHousehold.accounts.length > 0 ? (
                      selectedHousehold.accounts.map((account) => {
                        const isEditing = editingAccountId === account.id;
                        return (
                          <div
                            key={account.id}
                            data-account-id={account.id}
                            className="bg-white rounded-xl p-5 border border-gray-200 hover:border-emerald-300 transition-all"
                            onClick={
                              !isEditing
                                ? () => setSelectedAccount(account)
                                : undefined
                            }
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={
                                        accountFormData.new_account_number || ""
                                      }
                                      onChange={(e) =>
                                        setAccountFormData((prev) => ({
                                          ...prev,
                                          new_account_number: e.target.value,
                                        }))
                                      }
                                      placeholder="New Account Number"
                                      className="text-lg font-bold text-gray-900 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  ) : (
                                    <h4 className="text-lg font-bold text-gray-900">
                                      {account.transfer_info
                                        ?.old_account_number ||
                                        account.new_account_number ||
                                        "Account"}
                                      {account.new_account_number && (
                                        <span className="text-sm font-normal text-gray-600 ml-2">
                                          ({account.new_account_number})
                                        </span>
                                      )}
                                    </h4>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 flex-wrap mb-3">
                                  {isEditing ? (
                                    <>
                                      <input
                                        type="text"
                                        value={accountFormData.custodian || ""}
                                        onChange={(e) =>
                                          setAccountFormData((prev) => ({
                                            ...prev,
                                            custodian: e.target.value,
                                          }))
                                        }
                                        placeholder="Custodian"
                                        className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <input
                                        type="text"
                                        value={accountFormData.type || ""}
                                        onChange={(e) =>
                                          setAccountFormData((prev) => ({
                                            ...prev,
                                            type: e.target.value,
                                          }))
                                        }
                                        placeholder="Type"
                                        className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <input
                                        type="text"
                                        value={
                                          accountFormData.registration || ""
                                        }
                                        onChange={(e) =>
                                          setAccountFormData((prev) => ({
                                            ...prev,
                                            registration: e.target.value,
                                          }))
                                        }
                                        placeholder="Registration"
                                        className="text-sm px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      {account.custodian && (
                                        <span className="text-sm text-gray-600 font-medium">
                                          {account.custodian}
                                        </span>
                                      )}
                                      {account.type && (
                                        <span className="badge badge-info">
                                          {account.type}
                                        </span>
                                      )}
                                      {account.registration && (
                                        <>
                                          {account.fee_based_account !==
                                            undefined && (
                                            <span
                                              className={`badge ${account.fee_based_account ? "badge-success" : "badge-info"}`}
                                            >
                                              {account.fee_based_account
                                                ? "IWS"
                                                : "NFS"}
                                            </span>
                                          )}
                                          <span className="badge badge-warning">
                                            {account.registration}
                                          </span>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                                {!isEditing ? (
                                  <div className="flex flex-wrap gap-2">
                                    {showFormTypeSelectorForAccount ===
                                    account.id ? (
                                      <div
                                        className="flex flex-col gap-3 p-4 bg-white rounded-lg border border-gray-200 shadow-lg min-w-[320px] max-w-[400px]"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <label className="text-sm font-semibold text-gray-900 mb-2">
                                          Select Forms to Download:
                                        </label>

                                        {loadingAccountForms ? (
                                          <div className="flex items-center justify-center py-8">
                                            <Loader2 className="animate-spin h-6 w-6 text-emerald-600" />
                                            <span className="ml-2 text-sm text-gray-600">
                                              Loading forms...
                                            </span>
                                          </div>
                                        ) : accountFormsData[account.id] ? (
                                          <>
                                            <div
                                              className={`${accountFormsData[account.id].type === "iws" ? "border-b border-gray-200 pb-3" : "pb-2"}`}
                                            >
                                              <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wide">
                                                {accountFormsData[account.id]
                                                  .type === "iws"
                                                  ? "IWS Forms"
                                                  : "NFS Forms"}
                                              </div>
                                              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                                {(() => {
                                                  const formOrder =
                                                    accountFormsData[account.id]
                                                      .type === "iws"
                                                      ? iwsFormOrder
                                                      : nfsFormOrder;
                                                  const availableForms =
                                                    accountFormsData[account.id]
                                                      .forms;
                                                  return formOrder
                                                    .filter((formType) =>
                                                      availableForms.hasOwnProperty(
                                                        formType,
                                                      ),
                                                    )
                                                    .map((formType) => (
                                                      <label
                                                        key={formType}
                                                        className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          checked={
                                                            selectedFormsForAccount[
                                                              account.id
                                                            ]?.[formType] ||
                                                            false
                                                          }
                                                          onChange={() =>
                                                            handleFormToggle(
                                                              account.id,
                                                              formType,
                                                            )
                                                          }
                                                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                                        />
                                                        <span className="text-sm text-gray-700">
                                                          {getFormDisplayName(
                                                            formType,
                                                          )}
                                                        </span>
                                                        {accountFormsData[
                                                          account.id
                                                        ].forms[formType] ===
                                                          true && (
                                                          <span className="ml-auto text-xs text-emerald-600 font-medium">
                                                            Auto-selected
                                                          </span>
                                                        )}
                                                      </label>
                                                    ));
                                                })()}
                                              </div>
                                            </div>

                                            <div className="flex gap-2 pt-2 border-t border-gray-200">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleBulkDownloadForms(
                                                    account.id,
                                                  );
                                                }}
                                                className="btn-primary text-sm px-3 py-1.5 flex items-center gap-1 flex-1 justify-center"
                                                disabled={
                                                  downloadingAccountId ===
                                                  account.id
                                                }
                                              >
                                                {downloadingAccountId ===
                                                account.id ? (
                                                  <>
                                                    <Loader2 className="animate-spin h-3.5 w-3.5" />
                                                    Downloading...
                                                  </>
                                                ) : (
                                                  <>
                                                    <FileDown className="w-3.5 h-3.5" />
                                                    Download Selected
                                                  </>
                                                )}
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setShowFormTypeSelectorForAccount(
                                                    null,
                                                  );
                                                  setSelectedFormsForAccount(
                                                    (prev) => {
                                                      const newState = {
                                                        ...prev,
                                                      };
                                                      delete newState[
                                                        account.id
                                                      ];
                                                      return newState;
                                                    },
                                                  );
                                                  setAccountFormsData(
                                                    (prev) => {
                                                      const newState = {
                                                        ...prev,
                                                      };
                                                      delete newState[
                                                        account.id
                                                      ];
                                                      return newState;
                                                    },
                                                  );
                                                }}
                                                className="btn-ghost text-sm px-3 py-1.5"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </>
                                        ) : (
                                          <div className="text-sm text-gray-500 text-center py-4">
                                            {loadingAccountForms
                                              ? "Loading forms..."
                                              : "No forms data available."}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchAccountForms(account.id);
                                        }}
                                        className="btn-secondary text-sm px-3 py-1 flex items-center gap-1"
                                        disabled={
                                          downloadingAccountId === account.id ||
                                          loadingAccountForms ||
                                          !accountFormsData[account.id]
                                        }
                                      >
                                        {loadingAccountForms ? (
                                          <>
                                            <Loader2 className="animate-spin h-3.5 w-3.5" />
                                            Loading...
                                          </>
                                        ) : (
                                          <>
                                            <FileDown className="w-3.5 h-3.5" />
                                            Populate Forms
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex gap-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingAccountId(null);
                                        setAccountFormData({});
                                      }}
                                      className="btn-ghost text-sm px-3 py-1"
                                      disabled={savingAccount}
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSaveAccount(account.id);
                                      }}
                                      className="btn-primary text-sm px-3 py-1"
                                      disabled={savingAccount}
                                    >
                                      {savingAccount ? "Saving..." : "Save"}
                                    </button>
                                  </div>
                                )}
                              </div>
                              {isEditing ? (
                                <div className="text-right">
                                  <div className="text-sm text-gray-500 font-medium mb-1">
                                    Account Value
                                  </div>
                                  <input
                                    type="number"
                                    value={accountFormData.account_value || ""}
                                    onChange={(e) =>
                                      setAccountFormData((prev) => ({
                                        ...prev,
                                        account_value: e.target.value,
                                      }))
                                    }
                                    className="text-xl font-bold text-emerald-600 px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 w-32"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              ) : (
                                account.account_value && (
                                  <div className="text-right">
                                    <div className="text-sm text-gray-500 font-medium mb-1">
                                      Account Value
                                    </div>
                                    <div className="text-xl font-bold text-emerald-600">
                                      ${account.account_value.toLocaleString()}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
                              {isEditing ? (
                                <>
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium mb-1">
                                      Fee Billed %
                                    </div>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={
                                        accountFormData.fee_billed_percentage ??
                                        ""
                                      }
                                      onChange={(e) =>
                                        setAccountFormData((prev) => ({
                                          ...prev,
                                          fee_billed_percentage: e.target.value,
                                        }))
                                      }
                                      className="w-full px-2 py-1 border border-gray-300 rounded-lg text-sm"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium mb-1">
                                      Features
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      <label className="flex items-center gap-1 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={
                                            accountFormData.margin || false
                                          }
                                          onChange={(e) =>
                                            setAccountFormData((prev) => ({
                                              ...prev,
                                              margin: e.target.checked,
                                            }))
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        Margin
                                      </label>
                                      <label className="flex items-center gap-1 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={
                                            accountFormData.options || false
                                          }
                                          onChange={(e) =>
                                            setAccountFormData((prev) => ({
                                              ...prev,
                                              options: e.target.checked,
                                            }))
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        Options
                                      </label>
                                      <label className="flex items-center gap-1 text-xs">
                                        <input
                                          type="checkbox"
                                          checked={
                                            accountFormData.check_writing ||
                                            false
                                          }
                                          onChange={(e) =>
                                            setAccountFormData((prev) => ({
                                              ...prev,
                                              check_writing: e.target.checked,
                                            }))
                                          }
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        Check Writing
                                      </label>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {account.fee_billed_percentage != null && (
                                    <div>
                                      <div className="text-xs text-gray-500 font-medium mb-1">
                                        Fee Billed
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {account.fee_billed_percentage}%
                                      </div>
                                    </div>
                                  )}
                                  <div>
                                    <div className="text-xs text-gray-500 font-medium mb-1">
                                      Features
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {account.margin && (
                                        <span className="badge badge-info text-xs">
                                          Margin
                                        </span>
                                      )}
                                      {account.options && (
                                        <span className="badge badge-info text-xs">
                                          Options
                                        </span>
                                      )}
                                      {account.check_writing && (
                                        <span className="badge badge-info text-xs">
                                          Check Writing
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            {!isEditing && (
                              <div className="mt-3 flex items-center gap-2 text-emerald-600 text-sm font-medium">
                                <span>View Details</span>
                                <ChevronRight className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="bg-gray-50 rounded-xl p-6 text-center">
                        <p className="text-gray-500 font-medium">
                          No accounts found
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Account Detail Modal */}
      {selectedAccount && (
        <div
          className="modal-overlay"
          onClick={() => {
            setSelectedAccount(null);
            setEditingAccountId(null);
            setAccountFormData({});
          }}
        >
          <div
            className="modal-content max-w-6xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white via-blue-50/10 to-emerald-50/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6 pb-5 border-b border-blue-200/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-3xl font-extrabold bg-gradient-to-r from-gray-900 via-emerald-700 to-blue-700 bg-clip-text text-transparent">
                  Account:{" "}
                  {editingAccountId === selectedAccount.id ? (
                    <>
                      {accountFormData.old_account_number ||
                        accountFormData.new_account_number ||
                        "Account"}
                      {accountFormData.new_account_number && (
                        <span className="text-xl font-normal text-gray-600 ml-2">
                          ({accountFormData.new_account_number})
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {selectedAccount.transfer_info?.old_account_number ||
                        selectedAccount.new_account_number ||
                        "Account"}
                      {selectedAccount.new_account_number && (
                        <span className="text-xl font-normal text-gray-600 ml-2">
                          ({selectedAccount.new_account_number})
                        </span>
                      )}
                    </>
                  )}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setSelectedAccount(null);
                    setEditingAccountId(null);
                    setAccountFormData({});
                  }}
                  className="btn-ghost p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Basic Account Information */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  Account Information
                </h3>
                {(() => {
                  const isEditing = editingAccountId === selectedAccount.id;
                  return (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Existing Account Number
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.old_account_number || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                old_account_number: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.transfer_info?.old_account_number ||
                              "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          New Account Number
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.new_account_number || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                new_account_number: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.new_account_number || "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Custodian
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.custodian || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                custodian: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.custodian || "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Registration
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.registration || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                registration: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            {selectedAccount.fee_based_account !==
                              undefined && (
                              <span
                                className={`badge ${selectedAccount.fee_based_account ? "badge-success" : "badge-info"}`}
                              >
                                {selectedAccount.fee_based_account
                                  ? "IWS"
                                  : "NFS"}
                              </span>
                            )}
                            <div className="text-lg font-semibold text-gray-900">
                              {selectedAccount.registration || "N/A"}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Type
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.type || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                type: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.type || "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Account Value
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={accountFormData.account_value || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                account_value: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-emerald-600">
                            {selectedAccount.account_value
                              ? `$${selectedAccount.account_value.toLocaleString()}`
                              : "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          Fee Billed Percentage
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={accountFormData.fee_billed_percentage ?? ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                fee_billed_percentage: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.fee_billed_percentage != null
                              ? `${selectedAccount.fee_billed_percentage}%`
                              : "N/A"}
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-1">
                          New Rep ID
                        </div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={accountFormData.new_rep_id || ""}
                            onChange={(e) =>
                              setAccountFormData((prev) => ({
                                ...prev,
                                new_rep_id: e.target.value,
                              }))
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          />
                        ) : (
                          <div className="text-lg font-semibold text-gray-900">
                            {selectedAccount.new_rep_id || "N/A"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Financial Details */}
                {(() => {
                  const isEditing = editingAccountId === selectedAccount.id;
                  return (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Financial Details
                      </h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs text-gray-500 mb-1">
                            Proceeds
                          </div>
                          {isEditing ? (
                            <input
                              type="number"
                              value={accountFormData.proceeds || ""}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  proceeds: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {selectedAccount.proceeds
                                ? `$${selectedAccount.proceeds.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">
                            Security Purchases
                          </div>
                          {isEditing ? (
                            <input
                              type="number"
                              value={accountFormData.security_purchases || ""}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  security_purchases: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {selectedAccount.security_purchases
                                ? `$${selectedAccount.security_purchases.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 mb-1">
                            Distribution Income
                          </div>
                          {isEditing ? (
                            <input
                              type="number"
                              value={accountFormData.distribution_income || ""}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  distribution_income: e.target.value,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                            />
                          ) : (
                            <div className="text-sm text-gray-900">
                              {selectedAccount.distribution_income
                                ? `$${selectedAccount.distribution_income.toLocaleString()}`
                                : "N/A"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Account Features */}
                {(() => {
                  const isEditing = editingAccountId === selectedAccount.id;
                  return (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Account Features
                      </h4>
                      {isEditing ? (
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={accountFormData.margin || false}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  margin: e.target.checked,
                                }))
                              }
                              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700">
                              Margin
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={accountFormData.options || false}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  options: e.target.checked,
                                }))
                              }
                              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700">
                              Options
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={
                                accountFormData.fee_based_account || false
                              }
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  fee_based_account: e.target.checked,
                                }))
                              }
                              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700">
                              Fee Based Account
                            </span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={accountFormData.check_writing || false}
                              onChange={(e) =>
                                setAccountFormData((prev) => ({
                                  ...prev,
                                  check_writing: e.target.checked,
                                }))
                              }
                              className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                            />
                            <span className="text-sm text-gray-700">
                              Check Writing
                            </span>
                          </label>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {selectedAccount.margin && (
                            <span className="badge badge-success">Margin</span>
                          )}
                          {selectedAccount.options && (
                            <span className="badge badge-success">Options</span>
                          )}
                          {selectedAccount.fee_based_account && (
                            <span className="badge badge-success">
                              Fee Based Account
                            </span>
                          )}
                          {selectedAccount.check_writing && (
                            <span className="badge badge-success">
                              Check Writing
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Account Owners */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                return selectedAccount.owners_info || isEditing ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="owner_info"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Account Owners
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Owners Info (JSON Array)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.owners_info || [],
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                owners_info: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="8"
                          placeholder='[{"id": "", "type": "", "share": ""}, ...]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON array format
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedAccount.owners_info?.map((owner, idx) => {
                          // Find member name from household members
                          const member = selectedHousehold.members?.find(
                            (m) => m.id === owner.id,
                          );
                          const memberName = member
                            ? [
                                member.personal_info?.first_name,
                                member.personal_info?.last_name,
                              ]
                                .filter(Boolean)
                                .join(" ")
                            : "Unknown Member";

                          return (
                            <div
                              key={idx}
                              className="bg-gray-50 rounded-lg p-4"
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {memberName}
                                  </div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    Type:{" "}
                                    <span className="font-medium">
                                      {owner.type}
                                    </span>
                                    {owner.share &&
                                      !selectedAccount.registration
                                        ?.toLowerCase()
                                        .includes("trust") && (
                                        <span className="ml-3">
                                          Share: {owner.share}%
                                        </span>
                                      )}
                                  </div>
                                </div>
                                <span
                                  className={`badge ${owner.type === "primary" ? "badge-success" : "badge-info"}`}
                                >
                                  {owner.type}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Transfer Information */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                return selectedAccount.transfer_info || isEditing ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="transfer_info"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Transfer Information
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Transfer Info (JSON)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.transfer_info || {},
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                transfer_info: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="10"
                          placeholder='{"old_account_number": "", "new_account_number": "", ...}'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON format
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          {selectedAccount.transfer_info
                            ?.old_account_number && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Existing Account Number
                              </div>
                              <div className="text-sm text-gray-900">
                                {
                                  selectedAccount.transfer_info
                                    .old_account_number
                                }
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info
                            ?.new_account_number && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                New Account Number
                              </div>
                              <div className="text-sm text-gray-900">
                                {
                                  selectedAccount.transfer_info
                                    .new_account_number
                                }
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info?.account_owner && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Account Owner
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.transfer_info.account_owner}
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info
                            ?.delivering_firm_name && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Delivering Firm
                              </div>
                              <div className="text-sm text-gray-900">
                                {
                                  selectedAccount.transfer_info
                                    .delivering_firm_name
                                }
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info?.registration_type && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Registration Type
                              </div>
                              <div className="text-sm text-gray-900">
                                {
                                  selectedAccount.transfer_info
                                    .registration_type
                                }
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info?.transfer_type && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Transfer Type
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.transfer_info.transfer_type}
                              </div>
                            </div>
                          )}
                          {selectedAccount.transfer_info?.is_full_transfer !==
                            null &&
                            selectedAccount.transfer_info?.is_full_transfer !==
                              undefined && (
                              <div>
                                <div className="text-sm text-gray-500 font-medium mb-1">
                                  Full Transfer
                                </div>
                                <div className="text-sm text-gray-900">
                                  {selectedAccount.transfer_info
                                    .is_full_transfer
                                    ? "Yes"
                                    : "No"}
                                </div>
                              </div>
                            )}
                        </div>
                        {selectedAccount.transfer_info?.address && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="text-sm font-semibold text-gray-700 mb-2">
                              Transfer Address
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-900">
                              {selectedAccount.transfer_info.address
                                .address_line_1 && (
                                <div>
                                  {
                                    selectedAccount.transfer_info.address
                                      .address_line_1
                                  }
                                </div>
                              )}
                              {selectedAccount.transfer_info.address
                                .address_line_2 && (
                                <div>
                                  {
                                    selectedAccount.transfer_info.address
                                      .address_line_2
                                  }
                                </div>
                              )}
                              <div>
                                {[
                                  selectedAccount.transfer_info.address.city,
                                  selectedAccount.transfer_info.address.state,
                                  selectedAccount.transfer_info.address
                                    .postal_code,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </div>
                              {selectedAccount.transfer_info.address
                                .country && (
                                <div>
                                  {
                                    selectedAccount.transfer_info.address
                                      .country
                                  }
                                </div>
                              )}
                              {selectedAccount.transfer_info.address.phone && (
                                <div className="mt-1 text-gray-600">
                                  {selectedAccount.transfer_info.address.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {selectedAccount.transfer_info?.notes && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="text-sm font-semibold text-gray-700 mb-2">
                              Notes
                            </div>
                            <div className="text-sm text-gray-900">
                              {selectedAccount.transfer_info.notes}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Beneficiaries */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                // Don't show beneficiaries at account level if this is a Trust account (they'll appear in Trust Info section)
                return (selectedAccount.beneficiaries || isEditing) &&
                  !selectedAccount.trust_info ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="beneficiaries"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Beneficiaries (
                      {selectedAccount.beneficiaries?.length || 0})
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Beneficiaries (JSON Array)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.beneficiaries || [],
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                beneficiaries: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="12"
                          placeholder='[{"first_name": "", "last_name": "", "type": "", ...}, ...]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON array format
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {selectedAccount.beneficiaries?.map(
                          (beneficiary, idx) => {
                            const fullName = [
                              beneficiary.first_name,
                              beneficiary.middle_name,
                              beneficiary.last_name,
                            ]
                              .filter(Boolean)
                              .join(" ");

                            return (
                              <div
                                key={idx}
                                className="bg-gray-50 rounded-lg p-4"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <h4 className="font-semibold text-gray-900">
                                    {fullName || "N/A"}
                                  </h4>
                                  <span
                                    className={`badge ${beneficiary.type === "primary" ? "badge-success" : "badge-warning"}`}
                                  >
                                    {beneficiary.type}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                  {beneficiary.ssn && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        SSN
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {beneficiary.ssn}
                                      </div>
                                    </div>
                                  )}
                                  {beneficiary.dob && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Date of Birth
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {formatDate(beneficiary.dob)}
                                      </div>
                                    </div>
                                  )}
                                  {beneficiary.percentage_share && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Percentage Share
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {beneficiary.percentage_share}%
                                      </div>
                                    </div>
                                  )}
                                  {beneficiary.relationship && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Relationship to Account Owner
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {beneficiary.relationship}
                                      </div>
                                    </div>
                                  )}
                                  {beneficiary.country_of_citizenship && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Country of Citizenship
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {beneficiary.country_of_citizenship}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Trusted Contacts */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                // Don't show trusted contacts at account level if this is a Trust account (they'll appear in Trust Info section)
                return (selectedAccount.trusted_contacts || isEditing) &&
                  !selectedAccount.trust_info ? (
                  <div className="bg-white rounded-xl p-6 border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Trusted Contacts (
                      {selectedAccount.trusted_contacts?.length || 0})
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Trusted Contacts (JSON Array)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.trusted_contacts || [],
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                trusted_contacts: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="12"
                          placeholder='[{"name": "", "email": "", "relationship": "", ...}, ...]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON array format
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {selectedAccount.trusted_contacts?.map(
                          (contact, idx) => {
                            const contactFullName =
                              contact.first_name || contact.name
                                ? [
                                    contact.first_name ||
                                      (contact.name
                                        ? contact.name.split(" ")[0]
                                        : ""),
                                    contact.middle_name ||
                                      (contact.name &&
                                      contact.name.split(" ").length > 2
                                        ? contact.name
                                            .split(" ")
                                            .slice(1, -1)
                                            .join(" ")
                                        : ""),
                                    contact.last_name ||
                                      (contact.name
                                        ? contact.name.split(" ").slice(-1)[0]
                                        : ""),
                                  ]
                                    .filter(Boolean)
                                    .join(" ") ||
                                  contact.name ||
                                  "N/A"
                                : "N/A";
                            return (
                              <div
                                key={idx}
                                className="bg-gray-50 rounded-lg p-4"
                              >
                                <h4 className="font-semibold text-gray-900 mb-3">
                                  {contactFullName}
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                  {contact.email && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Email
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {contact.email}
                                      </div>
                                    </div>
                                  )}
                                  {contact.relationship && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Relationship to Account Owner
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {contact.relationship}
                                      </div>
                                    </div>
                                  )}
                                  {contact.country_of_citizenship && (
                                    <div>
                                      <div className="text-xs text-gray-500 mb-1">
                                        Country of Citizenship
                                      </div>
                                      <div className="text-sm text-gray-900">
                                        {contact.country_of_citizenship}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {contact.address && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="text-xs font-semibold text-gray-600 mb-1">
                                      Address
                                    </div>
                                    <div className="text-sm text-gray-900">
                                      {contact.address.address_line_1 && (
                                        <div>
                                          {contact.address.address_line_1}
                                        </div>
                                      )}
                                      {contact.address.address_line_2 && (
                                        <div>
                                          {contact.address.address_line_2}
                                        </div>
                                      )}
                                      <div>
                                        {[
                                          contact.address.city,
                                          contact.address.province,
                                          contact.address.postal_code,
                                        ]
                                          .filter(Boolean)
                                          .join(", ")}
                                      </div>
                                      {contact.address.country && (
                                        <div>{contact.address.country}</div>
                                      )}
                                      {contact.address.phone && (
                                        <div className="mt-1 text-gray-600">
                                          {contact.address.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Risk Tolerance */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                return selectedAccount.risk_tolerance || isEditing ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="risk_tolerance"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Risk Tolerance
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Risk Tolerance (JSON)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.risk_tolerance || {},
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                risk_tolerance: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="15"
                          placeholder='{"risk_tolerance": "", "time_horizon": "", ...}'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON format
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          {selectedAccount.risk_tolerance?.risk_tolerance && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Risk Tolerance
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.risk_tolerance.risk_tolerance}
                              </div>
                            </div>
                          )}
                          {selectedAccount.risk_tolerance?.time_horizon && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Time Horizon
                              </div>
                              <div className="text-sm text-gray-900">
                                {formatTimeHorizon(
                                  selectedAccount.risk_tolerance.time_horizon,
                                )}
                              </div>
                            </div>
                          )}
                          {selectedAccount.risk_tolerance?.decision_making && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Decision Making
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.risk_tolerance.decision_making}
                              </div>
                            </div>
                          )}
                          {showUseOfFunds(brokerageFlags) &&
                            selectedAccount.risk_tolerance?.use_of_funds && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Use of Funds
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.risk_tolerance.use_of_funds}
                              </div>
                            </div>
                          )}
                        </div>
                        {selectedAccount.risk_tolerance?.invest_objectives &&
                          Array.isArray(
                            selectedAccount.risk_tolerance.invest_objectives,
                          ) &&
                          selectedAccount.risk_tolerance.invest_objectives
                            .length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-sm font-semibold text-gray-700 mb-2">
                                Investment Objectives
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {selectedAccount.risk_tolerance.invest_objectives.map(
                                  (obj, idx) => (
                                    <span
                                      key={idx}
                                      className="badge badge-info"
                                    >
                                      {obj}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        {showSourceOfFunds(brokerageFlags) &&
                          selectedAccount.risk_tolerance?.source_of_funds &&
                          Array.isArray(
                            selectedAccount.risk_tolerance.source_of_funds,
                          ) &&
                          selectedAccount.risk_tolerance.source_of_funds
                            .length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <div className="text-sm font-semibold text-gray-700 mb-2">
                                Source of Funds
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {selectedAccount.risk_tolerance.source_of_funds.map(
                                  (source, idx) => (
                                    <span
                                      key={idx}
                                      className="badge badge-warning"
                                    >
                                      {source}
                                    </span>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        {showLiquidityRequirements(brokerageFlags) &&
                          selectedAccount.risk_tolerance
                            ?.liquidity_requirements && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="text-sm font-semibold text-gray-700 mb-2">
                              Liquidity Requirements
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              {selectedAccount.risk_tolerance
                                .liquidity_requirements.amount && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Amount Range
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    $
                                    {formatLiquidityAmount(
                                      selectedAccount.risk_tolerance
                                        .liquidity_requirements.amount,
                                    )}
                                  </div>
                                </div>
                              )}
                              {selectedAccount.risk_tolerance
                                .liquidity_requirements.time_horizon && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Time Horizon Range
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {formatTimeHorizon(
                                      selectedAccount.risk_tolerance
                                        .liquidity_requirements.time_horizon,
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : null;
              })()}

              {(() => {
                const banks = getAccountBanks(selectedAccount);
                return banks.length > 0 ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="bank_account_details"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Bank Accounts
                    </h3>
                    <BankAccountAccordion
                      banks={banks}
                      isEditing={false}
                      fieldsReadOnly
                      showAccountNumberMasked
                    />
                  </div>
                ) : null;
              })()}

              {/* Trust Information - Only for Trust Accounts */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                const isTrustAccount =
                  selectedAccount.registration
                    ?.toLowerCase()
                    .includes("trust") || selectedAccount.trust_info;
                return isTrustAccount &&
                  (selectedAccount.trust_info || isEditing) ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="trust_info"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Trust Information
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Trust Information (JSON)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.trust_info || {},
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                trust_info: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="20"
                          placeholder='{"trust_name": "", "grantor_or_settler": "", "trustees": [], ...}'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON format
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Basic Trust Information */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                          {(selectedAccount.trust_info?.trust_name ||
                            selectedAccount.trust_info?.trust_title) && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Trust Name
                              </div>
                              <div className="text-sm font-semibold text-gray-900">
                                {selectedAccount.trust_info.trust_name ||
                                  selectedAccount.trust_info.trust_title}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.trust_title && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Trust title (as per document)
                              </div>
                              <div className="text-sm font-semibold text-gray-900">
                                {selectedAccount.trust_info.trust_title}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.ssn_or_tin && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                SSN/TIN
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.trust_info.ssn_or_tin}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.execution_date && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Execution Date
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.trust_info.execution_date}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.amended_date && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Amended Date
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.trust_info.amended_date}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.trust_state && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Trust State
                              </div>
                              <div className="text-sm text-gray-900">
                                {resolveStateName(selectedAccount.trust_info.trust_state) ||
                                  selectedAccount.trust_info.trust_state}
                              </div>
                            </div>
                          )}
                          {selectedAccount.trust_info?.descendant_name && (
                            <div>
                              <div className="text-sm text-gray-500 font-medium mb-1">
                                Decedent Name
                              </div>
                              <div className="text-sm text-gray-900">
                                {selectedAccount.trust_info.descendant_name}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Grantor/Settlor */}
                        {selectedAccount.trust_info?.grantor_or_settler && (
                          <div className="mb-6 pt-6 border-t border-gray-200">
                            <div className="text-sm font-semibold text-gray-700 mb-3">
                              Grantor/Settlor
                            </div>
                            <div className="text-sm text-gray-900">
                              {(() => {
                                // Try to find the member name from household members
                                const grantorId =
                                  selectedAccount.trust_info.grantor_or_settler;
                                if (selectedHousehold?.members) {
                                  const grantor =
                                    selectedHousehold.members.find(
                                      (m) => m.id === grantorId,
                                    );
                                  if (grantor?.personal_info) {
                                    const {
                                      first_name,
                                      middle_name,
                                      last_name,
                                    } = grantor.personal_info;
                                    return [first_name, middle_name, last_name]
                                      .filter(Boolean)
                                      .join(" ");
                                  }
                                }
                                return grantorId || "N/A";
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Trustees */}
                        {selectedAccount.trust_info?.trustees &&
                          Array.isArray(selectedAccount.trust_info.trustees) &&
                          selectedAccount.trust_info.trustees.length > 0 && (
                            <div className="mb-6 pt-6 border-t border-gray-200">
                              <div className="text-sm font-semibold text-gray-700 mb-3">
                                Trustees (
                                {selectedAccount.trust_info.trustees.length})
                              </div>
                              <div className="space-y-3">
                                {selectedAccount.trust_info.trustees.map(
                                  (trustee, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-gray-50 rounded-lg p-4"
                                    >
                                      <div className="text-sm font-semibold text-gray-900 mb-2">
                                        {(() => {
                                          // Try to find the member name from household members
                                          const trusteeId =
                                            trustee.id || trustee;
                                          if (selectedHousehold?.members) {
                                            const member =
                                              selectedHousehold.members.find(
                                                (m) => m.id === trusteeId,
                                              );
                                            if (member?.personal_info) {
                                              const {
                                                first_name,
                                                middle_name,
                                                last_name,
                                              } = member.personal_info;
                                              return [
                                                first_name,
                                                middle_name,
                                                last_name,
                                              ]
                                                .filter(Boolean)
                                                .join(" ");
                                            }
                                          }
                                          return (
                                            trusteeId || `Trustee ${idx + 1}`
                                          );
                                        })()}
                                      </div>
                                      {trustee.relationship_to_grantor && (
                                        <div className="text-xs text-gray-600">
                                          Relationship to Grantor:{" "}
                                          {trustee.relationship_to_grantor}
                                        </div>
                                      )}
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                        {/* Investment Powers & Restrictions */}
                        {(selectedAccount.trust_info?.investment_powers ||
                          selectedAccount.trust_info?.restrictions) && (
                          <div className="mb-6 pt-6 border-t border-gray-200">
                            {selectedAccount.trust_info.investment_powers && (
                              <div className="mb-3">
                                <div className="text-sm font-semibold text-gray-700 mb-1">
                                  Investment Powers
                                </div>
                                <div className="text-sm text-gray-900">
                                  {selectedAccount.trust_info.investment_powers}
                                </div>
                              </div>
                            )}
                            {selectedAccount.trust_info.restrictions && (
                              <div>
                                <div className="text-sm font-semibold text-gray-700 mb-1">
                                  Restrictions
                                </div>
                                <div className="text-sm text-gray-900">
                                  {selectedAccount.trust_info.restrictions}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Trust Beneficiaries */}
                        {selectedAccount.trust_info?.beneficiaries &&
                          Array.isArray(
                            selectedAccount.trust_info.beneficiaries,
                          ) &&
                          selectedAccount.trust_info.beneficiaries.length >
                            0 && (
                            <div className="pt-6 border-t border-gray-200">
                              <div className="text-sm font-semibold text-gray-700 mb-3">
                                Trust Beneficiaries (
                                {
                                  selectedAccount.trust_info.beneficiaries
                                    .length
                                }
                                )
                              </div>
                              <div className="space-y-3">
                                {selectedAccount.trust_info.beneficiaries.map(
                                  (beneficiary, idx) => {
                                    const fullName = [
                                      beneficiary.first_name,
                                      beneficiary.middle_name,
                                      beneficiary.last_name,
                                    ]
                                      .filter(Boolean)
                                      .join(" ");

                                    return (
                                      <div
                                        key={idx}
                                        className="bg-gray-50 rounded-lg p-4"
                                      >
                                        <div className="flex items-start justify-between mb-2">
                                          <h4 className="font-semibold text-gray-900">
                                            {fullName || "N/A"}
                                          </h4>
                                          <span
                                            className={`badge ${beneficiary.type === "primary" ? "badge-success" : "badge-warning"}`}
                                          >
                                            {beneficiary.type || "N/A"}
                                          </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 mt-3">
                                          {beneficiary.ssn && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                SSN
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {beneficiary.ssn}
                                              </div>
                                            </div>
                                          )}
                                          {beneficiary.dob && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Date of Birth
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {beneficiary.dob}
                                              </div>
                                            </div>
                                          )}
                                          {beneficiary.percentage_share && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Percentage Share
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {beneficiary.percentage_share}%
                                              </div>
                                            </div>
                                          )}
                                          {beneficiary.relationship && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Relationship to Account Owner
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {beneficiary.relationship}
                                              </div>
                                            </div>
                                          )}
                                          {beneficiary.country_of_citizenship && (
                                            <div>
                                              <div className="text-xs text-gray-500 mb-1">
                                                Country of Citizenship
                                              </div>
                                              <div className="text-sm text-gray-900">
                                                {
                                                  beneficiary.country_of_citizenship
                                                }
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}
                      </>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Withdrawals */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                return selectedAccount.withdrawals || isEditing ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="withdrawals"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Withdrawals ({selectedAccount.withdrawals?.length || 0})
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Withdrawals (JSON Array)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.withdrawals || [],
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                withdrawals: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="12"
                          placeholder='[{"type": "", "amount": "", "frequency": "", ...}, ...]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON array format
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {selectedAccount.withdrawals?.map((withdrawal, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 gap-4">
                              {withdrawal.type && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Type
                                  </div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {withdrawal.type}
                                  </div>
                                </div>
                              )}
                              {withdrawal.amount && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Amount
                                  </div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    ${withdrawal.amount.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              {withdrawal.frequency && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Frequency
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {withdrawal.frequency}
                                  </div>
                                </div>
                              )}
                              {withdrawal.method && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Method
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {withdrawal.method}
                                  </div>
                                </div>
                              )}
                              {withdrawal.start_date && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Start Date
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(withdrawal.start_date)}
                                  </div>
                                </div>
                              )}
                              {withdrawal.end_date && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    End Date
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(withdrawal.end_date)}
                                  </div>
                                </div>
                              )}
                              {withdrawal.destination && (
                                <div className="col-span-2">
                                  <div className="text-xs text-gray-500 mb-1">
                                    Destination
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {withdrawal.destination}
                                  </div>
                                </div>
                              )}
                              {(withdrawal.federal_tax_withholding ||
                                withdrawal.state_tax_withholding) && (
                                <div className="col-span-2 pt-2 border-t border-gray-200">
                                  <div className="text-xs text-gray-500 mb-1">
                                    Tax Withholding
                                  </div>
                                  <div className="text-xs text-gray-700">
                                    Federal:{" "}
                                    {withdrawal.federal_tax_withholding ||
                                      "N/A"}{" "}
                                    | State:{" "}
                                    {withdrawal.state_tax_withholding || "N/A"}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}

              {/* Deposits */}
              {(() => {
                const isEditing = editingAccountId === selectedAccount.id;
                return selectedAccount.deposits || isEditing ? (
                  <div
                    className="bg-white rounded-xl p-6 border border-gray-100"
                    data-account-field="deposits"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      Deposits ({selectedAccount.deposits?.length || 0})
                    </h3>
                    {isEditing ? (
                      <div>
                        <div className="text-sm text-gray-500 font-medium mb-2">
                          Deposits (JSON Array)
                        </div>
                        <textarea
                          value={JSON.stringify(
                            accountFormData.deposits || [],
                            null,
                            2,
                          )}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setAccountFormData((prev) => ({
                                ...prev,
                                deposits: parsed,
                              }));
                            } catch (err) {
                              // Invalid JSON, keep the text for now
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-mono"
                          rows="12"
                          placeholder='[{"type": "", "amount": "", "frequency": "", ...}, ...]'
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter valid JSON array format
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {selectedAccount.deposits?.map((deposit, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-4">
                            <div className="grid grid-cols-2 gap-4">
                              {deposit.type && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Type
                                  </div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {deposit.type}
                                  </div>
                                </div>
                              )}
                              {deposit.amount && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Amount
                                  </div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    ${deposit.amount.toLocaleString()}
                                  </div>
                                </div>
                              )}
                              {deposit.frequency && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Frequency
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {deposit.frequency}
                                  </div>
                                </div>
                              )}
                              {deposit.method && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Method
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {deposit.method}
                                  </div>
                                </div>
                              )}
                              {deposit.start_date && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    Start Date
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(deposit.start_date)}
                                  </div>
                                </div>
                              )}
                              {deposit.end_date && (
                                <div>
                                  <div className="text-xs text-gray-500 mb-1">
                                    End Date
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {formatDate(deposit.end_date)}
                                  </div>
                                </div>
                              )}
                              {deposit.destination && (
                                <div className="col-span-2">
                                  <div className="text-xs text-gray-500 mb-1">
                                    Destination
                                  </div>
                                  <div className="text-sm text-gray-900">
                                    {deposit.destination}
                                  </div>
                                </div>
                              )}
                              {(deposit.federal_tax_withholding ||
                                deposit.state_tax_withholding) && (
                                <div className="col-span-2 pt-2 border-t border-gray-200">
                                  <div className="text-xs text-gray-500 mb-1">
                                    Tax Withholding
                                  </div>
                                  <div className="text-xs text-gray-700">
                                    Federal:{" "}
                                    {deposit.federal_tax_withholding || "N/A"} |
                                    State:{" "}
                                    {deposit.state_tax_withholding || "N/A"}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Modal */}
      {/* Delete Project Modal - Commented out */}
      {/* {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Project</h3>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-3">
                To confirm deletion, please type the project name: <strong>{project?.title}</strong>
              </p>
              <input
                type="text"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                placeholder="Enter project name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmationText("");
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProject}
                disabled={deleteConfirmationText !== project?.title || isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : (
                  "Confirm Delete"
                )}
              </button>
            </div>
          </div>
        </div>
      )} */}

      <CreateHouseholdsModal
        isOpen={showCreateHouseholdModal}
        onClose={() => setShowCreateHouseholdModal(false)}
        project={project}
        projectId={projectId}
        onSuccess={() => {
            fetchHouseholds(1);
            setExcelTriggerToken((n) => n + 1);
          }}
      />

      {/* Enrich Existing Households Modal */}
      {canAccessPrivilegedProjectActions && showEnrichHouseholdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 relative">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Upload className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-blue-700 bg-clip-text text-transparent flex-1">
                  Enrich Existing Households
                </h2>
                <button
                  onClick={() => {
                    setShowEnrichHouseholdModal(false);
                    setEnrichHouseholdAdvisorId("");
                    setEnrichHouseholdDragging(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Advisor Selection */}
              {project?.advisors && project.advisors.length > 0 && (
                <div className="mb-4">
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Select Advisor for this File *
                  </label>
                  <select
                    value={enrichHouseholdAdvisorId}
                    onChange={(e) =>
                      setEnrichHouseholdAdvisorId(e.target.value)
                    }
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                  >
                    <option value="">-- Select Advisor --</option>
                    {project.advisors.map((advisor, index) => (
                      <option
                        key={index}
                        value={advisor.user_id || String(index)}
                      >
                        {advisor.first_name} {advisor.last_name}{" "}
                        {advisor.g_number && `(G#${advisor.g_number})`}{" "}
                        {advisor.rr_code && `(RR:${advisor.rr_code})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* File Upload Area */}
              <label className="block">
                <div
                  onDragOver={handleEnrichHouseholdDragOver}
                  onDragLeave={handleEnrichHouseholdDragLeave}
                  onDrop={handleEnrichHouseholdDrop}
                  className={`mt-2 flex justify-center px-4 pt-6 pb-6 border-2 border-dashed rounded-xl transition-all duration-300 cursor-pointer ${
                    enrichHouseholdDragging
                      ? "border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100/50 shadow-lg scale-[1.02]"
                      : "border-blue-200 hover:border-blue-400 bg-gradient-to-br from-gray-50/50 to-blue-50/30 hover:shadow-md"
                  }`}
                >
                  <div className="space-y-2 text-center">
                    <div className="mx-auto w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <svg
                        className="h-6 w-6 text-white"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="flex text-sm text-gray-700 font-medium justify-center items-center gap-2">
                      <span className="relative cursor-pointer font-bold text-blue-600 hover:text-blue-700 transition-colors">
                        Upload file
                      </span>
                      <span className="text-gray-500">or</span>
                      <span className="text-gray-600">drag and drop</span>
                    </div>
                    <p className="text-xs text-gray-500 font-medium">
                      Excel file (.xlsx, .xls) (max 20MB)
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleEnrichHouseholdFileInputChange}
                    disabled={enrichHouseholdUploading}
                    className="hidden"
                  />
                </div>
              </label>

              {enrichHouseholdUploading && (
                <div className="mt-4 flex items-center gap-2 text-blue-600">
                  <Loader2 className="animate-spin h-5 w-5" />
                  <span className="font-medium text-sm">
                    Uploading file and starting processing...
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {canAccessPrivilegedProjectActions && (
        <ExportProjectDataModal
          isOpen={showExportProjectDataModal}
          onClose={() => setShowExportProjectDataModal(false)}
          projectId={projectId}
        />
      )}

      {/* Wizard Modal */}
      <HouseholdSetupWizard
        isOpen={showWizard}
        onClose={() => {
          setShowWizard(false);
          setNeedsAttentionRefreshKey((k) => k + 1);
        }}
        householdId={wizardHouseholdId}
        initialStatus={wizardInitialStatus}
        onComplete={handleWizardComplete}
        aumAvailability={project?.aum_availability}
        custodianTypes={project?.custodian_types}
      />

      {/* Upload Data Modal */}
      {showUploadDataModal && (
        <UploadDataModal
          isOpen={showUploadDataModal}
          onClose={() => setShowUploadDataModal(false)}
          selectedHousehold={selectedHousehold}
          projectId={projectId}
          onUploadSuccess={() => {
            // Refresh household data after successful upload
            if (selectedHousehold) {
              handleHouseholdClick(selectedHousehold.id);
            }
          }}
        />
      )}

      {/* Request Client Info Success Popup */}
      {showSendToClientSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Success
                </h3>
                <p className="text-sm text-gray-600">
                  Email sent to client successfully to{" "}
                  <span className="font-medium">{sendToClientEmail}</span>
                </p>
              </div>
              <button
                onClick={() => setShowSendToClientSuccess(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Request Client Info Error Popup */}
      {showSendToClientError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Error
                </h3>
                <p className="text-sm text-gray-600">
                  {sendToClientErrorMessage}
                </p>
              </div>
              <button
                onClick={() => setShowSendToClientError(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetail;
